import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { parseISO, addDays, startOfDay, isAfter } from "date-fns";
import {
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { AppointmentLocation } from "../../../../utils/types/session";
import {
    ClientEducation,
    ClientGender,
    ClientRace,
    ClientRelationship,
    ClientReligion,
    ClientSexualOrientation,
} from "../../../../utils/types/client";

import Accordion from "../../../Accordion/Accordion";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import {
    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS,
    MINOR_RISK_ASSESSMENT_LABELS,
} from "../../../../utils/constants";
import {
    IEditInitialDiagnosticEvaluationPayload,
    IGetClientNotes,
    ISessionNote,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditInitialDiagnosticEvaluationProps {
    noteDetails: ISessionNote;
    isMinor?: boolean;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function EditInitialDiagnosticEvaluation({
    noteDetails,
    isMinor = false,
}: EditInitialDiagnosticEvaluationProps) {
    // Local component state
    const [localDateOfNextSession, setLocalDateOfNextSession] =
        React.useState<Date | null>(
            noteDetails?.note_content?.date_of_next_session
                ? parseISO(noteDetails?.note_content?.date_of_next_session)
                : null
        );
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );
    const isSigning = React.useRef(false);

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        control,
        watch,
        setValue,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditInitialDiagnosticEvaluationPayload>({
        resolver: yupResolver(
            yup.object({
                duration: yup.string(),
                appointment_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Session location is required"),
                    otherwise: yup.string(),
                }),
                delivery_option: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Client teletherapy location is required"
                                  )
                                : field
                    ),
                provider_location: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Provider teletherapy location is required"
                                  )
                                : field
                    ),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_teletherapy_session_was_conducted_via_video: yup.bool(),
                other_delivery_option: yup
                    .string()
                    .when("delivery_option", (delivery_option, field) =>
                        delivery_option === "Other" && isSigning.current
                            ? field
                                  .trim()
                                  .required(
                                      "Other client teletherapy location is required"
                                  )
                            : field
                    ),
                other_provider_location: yup
                    .string()
                    .when("provider_location", (provider_location, field) =>
                        provider_location === "Other" && isSigning.current
                            ? field
                                  .trim()
                                  .required(
                                      "Other provider teletherapy location is required"
                                  )
                            : field
                    ),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                preferred_name: yup.string(),
                gender: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Gender is required"),
                    otherwise: yup.string(),
                }),
                other_gender: yup
                    .string()
                    .when("gender", (gender: string, field) =>
                        gender === "other" && isSigning.current
                            ? field.trim().required("Field is required")
                            : field
                    ),
                pronouns: yup.string(),
                religion: yup.string(),
                education: yup.string(),
                race: yup.string(),
                ethnicity: yup.string(),
                sexual_orientation: yup.string(),
                other_sexual_orientation: yup
                    .string()
                    .when(
                        "sexual_orientation",
                        (sexual_orientation: string, field) =>
                            sexual_orientation === "other" && isSigning.current
                                ? field.trim().required("Field is required")
                                : field
                    ),
                current_employment: yup.string(),
                current_employment_details: yup.string(),
                relationship_status: yup.string(),
                name_of_partner: yup.string(),
                emergency_contact_information: yup.string(),
                contact_information_for_both_parents: yup.string(),
                name_address_of_personal_physician: yup.string(),
                date_of_last_exam: yup.date().nullable(),
                legal_problems: yup.string(),
                what_are_the_problem_you_are_seeking_help_for: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required(
                                "What is the problem you are seeking help for is required"
                            )
                            .min(
                                20,
                                "What is the problem you are seeking help for must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                anxiety_frequency: yup.string(),
                anxiety_details: yup
                    .string()
                    .when("anxiety", (anxiety: boolean, field) =>
                        anxiety && isSigning.current
                            ? field
                                  .trim()
                                  .required("Anxiety details is required")
                                  .min(
                                      20,
                                      "Anxiety details must contain at least 20 characters"
                                  )
                            : field
                    ),
                panic_attacks_frequency: yup.string(),
                panic_attacks_details: yup
                    .string()
                    .when("panic_attacks", (panic_attacks: boolean, field) =>
                        panic_attacks && isSigning.current
                            ? field
                                  .trim()
                                  .required("Panic attacks details is required")
                                  .min(
                                      20,
                                      "Panic attacks details must contain at least 20 characters"
                                  )
                            : field
                    ),
                depression_frequency: yup.string(),
                depression_details: yup
                    .string()
                    .when("depression", (depression: boolean, field) =>
                        depression && isSigning.current
                            ? field
                                  .trim()
                                  .required("Depression details is required")
                                  .min(
                                      20,
                                      "Depression details must contain at least 20 characters"
                                  )
                            : field
                    ),
                selected_symptoms: yup.array().nullable(),
                other_symptoms: yup.string(),
                physical_symptoms: yup.string(),
                medication_physical_psychiatric: yup.string(),
                sleeping_habits_and_concerns: yup.string(),
                difficulties_with_appetite_or_eating_patterns: yup.string(),
                parents_stepparents_history: yup.string(),
                siblings_history: yup.string(),
                grandparents_history: yup.string(),
                children_history: yup.string(),
                other_family_members_history: yup.string(),
                family_members_alcohol_abuse: yup.array().nullable(),
                family_members_anxiety: yup.array().nullable(),
                family_members_bipolar_disorder: yup.array().nullable(),
                family_members_depression: yup.array().nullable(),
                family_members_ptsd: yup.array().nullable(),
                family_members_schizophrenia: yup.array().nullable(),
                family_members_substance_abuse: yup.array().nullable(),
                family_members_substance_abuse_substance: yup.string(),
                trauma_abuse_history: yup.string(),
                previous_mental_health_treatment_out_patient: yup.string(),
                impatient_psychiatric_hospitalization: yup.string(),
                relevant_medical_history: yup.string(),
                been_so_distressed_you_seriously_wished_to_end_your_life: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_been_so_distressed_you_seriously_wished_to_end_your_life:
                    yup
                        .string()
                        .when(
                            "been_so_distressed_you_seriously_wished_to_end_your_life",
                            (
                                been_so_distressed_you_seriously_wished_to_end_your_life,
                                field
                            ) =>
                                (been_so_distressed_you_seriously_wished_to_end_your_life ===
                                    "Today" ||
                                    been_so_distressed_you_seriously_wished_to_end_your_life ===
                                        "Recently") &&
                                isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Description field is required"
                                          )
                                          .min(
                                              20,
                                              "Description field must contain at least 20 characters"
                                          )
                                    : field
                        ),
                has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                    yup.string().when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                    yup
                        .string()
                        .when(
                            "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live",
                            (
                                has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live,
                                field
                            ) =>
                                has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ===
                                    "Yes" && isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Description field is required"
                                          )
                                          .min(
                                              20,
                                              "Description field must contain at least 20 characters"
                                          )
                                    : field
                        ),
                a_specific_plan_of_how_you_would_kill_yourself: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_a_specific_plan_of_how_you_would_kill_yourself: yup
                    .string()
                    .when(
                        "a_specific_plan_of_how_you_would_kill_yourself",
                        (
                            a_specific_plan_of_how_you_would_kill_yourself,
                            field
                        ) =>
                            a_specific_plan_of_how_you_would_kill_yourself ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                access_to_weapons_or_means_of_hurting_self: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_access_to_weapons_or_means_of_hurting_self: yup
                    .string()
                    .when(
                        "access_to_weapons_or_means_of_hurting_self",
                        (access_to_weapons_or_means_of_hurting_self, field) =>
                            access_to_weapons_or_means_of_hurting_self ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                made_serious_suicide_attempts: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                describe_made_serious_suicide_attempts: yup
                    .string()
                    .when(
                        "made_serious_suicide_attempts",
                        (made_serious_suicide_attempts, field) =>
                            made_serious_suicide_attempts === "Yes" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                purposely_done_something_to_hurt_yourself: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_done_something_to_hurt_yourself: yup
                    .string()
                    .when(
                        "purposely_done_something_to_hurt_yourself",
                        (purposely_done_something_to_hurt_yourself, field) =>
                            purposely_done_something_to_hurt_yourself ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                describe_do_you_have_access_to_guns: yup
                    .string()
                    .when(
                        "do_you_have_access_to_guns",
                        (do_you_have_access_to_guns, field) =>
                            do_you_have_access_to_guns === "Yes" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                heard_voices_telling_you_to_hurt_yourself: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_heard_voices_telling_you_to_hurt_yourself: yup
                    .string()
                    .when(
                        "heard_voices_telling_you_to_hurt_yourself",
                        (heard_voices_telling_you_to_hurt_yourself, field) =>
                            heard_voices_telling_you_to_hurt_yourself ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                had_relatives_who_attempted_or_committed_suicide: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_had_relatives_who_attempted_or_committed_suicide: yup
                    .string()
                    .when(
                        "had_relatives_who_attempted_or_committed_suicide",
                        (
                            had_relatives_who_attempted_or_committed_suicide,
                            field
                        ) =>
                            had_relatives_who_attempted_or_committed_suicide ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                had_thoughts_of_killing_or_seriously_hurting_someone: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_had_thoughts_of_killing_or_seriously_hurting_someone:
                    yup
                        .string()
                        .when(
                            "had_thoughts_of_killing_or_seriously_hurting_someone",
                            (
                                had_thoughts_of_killing_or_seriously_hurting_someone,
                                field
                            ) =>
                                had_thoughts_of_killing_or_seriously_hurting_someone ===
                                    "Yes" && isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Description field is required"
                                          )
                                          .min(
                                              20,
                                              "Description field must contain at least 20 characters"
                                          )
                                    : field
                        ),
                heard_voices_telling_you_to_hurt_others: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                describe_heard_voices_telling_you_to_hurt_others: yup
                    .string()
                    .when(
                        "heard_voices_telling_you_to_hurt_others",
                        (heard_voices_telling_you_to_hurt_others, field) =>
                            heard_voices_telling_you_to_hurt_others === "Yes" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                hurt_someone_or_destroyed_property_on_purpose: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_hurt_someone_or_destroyed_property_on_purpose: yup
                    .string()
                    .when(
                        "hurt_someone_or_destroyed_property_on_purpose",
                        (
                            hurt_someone_or_destroyed_property_on_purpose,
                            field
                        ) =>
                            hurt_someone_or_destroyed_property_on_purpose ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                slapped_kicked_punched_someone_with_intent_to_harm: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_slapped_kicked_punched_someone_with_intent_to_harm: yup
                    .string()
                    .when(
                        "slapped_kicked_punched_someone_with_intent_to_harm",
                        (
                            slapped_kicked_punched_someone_with_intent_to_harm,
                            field
                        ) =>
                            slapped_kicked_punched_someone_with_intent_to_harm ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                been_arrested_or_detained_for_violent_behavior: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                describe_been_arrested_or_detained_for_violent_behavior: yup
                    .string()
                    .when(
                        "been_arrested_or_detained_for_violent_behavior",
                        (
                            been_arrested_or_detained_for_violent_behavior,
                            field
                        ) =>
                            been_arrested_or_detained_for_violent_behavior ===
                                "Yes" && isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                been_to_jail_for_any_reason: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                describe_been_to_jail_for_any_reason: yup
                    .string()
                    .when(
                        "been_to_jail_for_any_reason",
                        (been_to_jail_for_any_reason, field) =>
                            been_to_jail_for_any_reason === "Yes" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                been_on_probation_for_any_reason: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                describe_been_on_probation_for_any_reason: yup
                    .string()
                    .when(
                        "been_on_probation_for_any_reason",
                        (been_on_probation_for_any_reason, field) =>
                            been_on_probation_for_any_reason === "Yes" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                do_you_have_access_to_guns: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                ever_run_away_from_home: yup.string().when([], {
                    is: () => isMinor && isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                describe_ever_run_away_from_home: yup
                    .string()
                    .when(
                        "ever_run_away_from_home",
                        (ever_run_away_from_home, field) =>
                            ever_run_away_from_home === "Yes" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                ever_been_bullied_or_bullied_someone: yup.string().when([], {
                    is: () => isMinor && isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                describe_ever_been_bullied_or_bullied_someone: yup
                    .string()
                    .when(
                        "ever_been_bullied_or_bullied_someone",
                        (ever_been_bullied_or_bullied_someone, field) =>
                            ever_been_bullied_or_bullied_someone === "Yes" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required("Description field is required")
                                      .min(
                                          20,
                                          "Description field must contain at least 20 characters"
                                      )
                                : field
                    ),
                mental_health_appearance: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Please select at least one option"),
                    otherwise: yup.array().nullable(),
                }),
                mental_health_movement: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Please select at least one option"),
                    otherwise: yup.array().nullable(),
                }),
                mental_health_speech_behaviour: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Please select at least on option"),
                    otherwise: yup.array().nullable(),
                }),
                mental_health_affect_mood: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Please select at least one option"),
                    otherwise: yup.array().nullable(),
                }),
                mental_health_attention_concentration_effort: yup
                    .array()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1)
                            .required("Please select at least one option"),
                        otherwise: yup.array().nullable(),
                    }),
                mental_health_presentation_of_self: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Please select at least one option"),
                    otherwise: yup.array().nullable(),
                }),
                mental_health_cognition: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Please select at least one option"),
                    otherwise: yup.array().nullable(),
                }),
                patients_ability_and_capability_to_respond_to_treatment: yup
                    .array()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1)
                            .required("This field is required"),
                        otherwise: yup.array().nullable(),
                    }),
                details_for_diminished_poor_limited_quality_patient_oriented_evidence:
                    yup
                        .string()
                        .when(
                            "patients_ability_and_capability_to_respond_to_treatment",
                            (
                                patients_ability_and_capability_to_respond_to_treatment: string[],
                                field
                            ) =>
                                patients_ability_and_capability_to_respond_to_treatment &&
                                patients_ability_and_capability_to_respond_to_treatment.includes(
                                    "Diminished/poor or limited-quality patient-oriented evidence"
                                )
                                    ? field.required("Details is required")
                                    : field
                        ),
                people_present_at_session: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("People present at session is required"),
                    otherwise: yup.array().nullable(),
                }),
                other_person_present_at_session: yup.string(),
                type_of_treatment_recommended: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Type of treatment recommended is required"),
                    otherwise: yup.array().nullable(),
                }),
                treatment_modality_intervention: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required(
                            "Treatment modality intervention is required"
                        ),
                    otherwise: yup.array().nullable(),
                }),
                other_treatment_modality_intervention: yup.string(),
                treatment_goal_one: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Treatment goal one is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_two: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Treatment goal two is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_three: yup.string(),
                frequency_of_sessions: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                other_frequency_of_sessions: yup
                    .string()
                    .when(
                        "frequency_of_sessions",
                        (frequency_of_sessions, field) =>
                            frequency_of_sessions === "Other" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required(
                                          "Other frequency of sessions is required"
                                      )
                                : field
                    ),
                date_of_next_session: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Date of next session is required")
                        .test(
                            "is-after-dos",
                            "Date of next session must be after the current session date",
                            function validateDateAfterDOS(value) {
                                if (!value) return false;
                                if (!noteDetails.date_of_service) return true;
                                const dos = startOfDay(
                                    parseISO(noteDetails.date_of_service)
                                );
                                const nextSession = startOfDay(value);
                                return isAfter(nextSession, dos);
                            }
                        ),
                    otherwise: yup.date().nullable(),
                }),
                subjective_report: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Subjective report is required")
                        .min(
                            20,
                            "Subjective report must contain at least 20 characters"
                        ),
                    otherwise: yup.string(),
                }),
                summary: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Summary is required")
                        .min(20, "Summary must contain at least 20 characters"),
                    otherwise: yup.string(),
                }),
                prognosis: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Prognosis is required")
                        .min(
                            20,
                            "Prognosis must contain at least 20 characters"
                        ),
                    otherwise: yup.string(),
                }),
                supervisory_intake_statement: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Supervisory intake statement is required"),
                    otherwise: yup.array().nullable(),
                }),
                medical_necessity_rationale: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Medical necessity rationale is required"),
                    otherwise: yup.array().nullable(),
                }),
                risk_review_supervision_plan: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Risk review supervision plan is required"),
                    otherwise: yup.array().nullable(),
                }),
                formal_safety_plan_details: yup.string(),
                higher_level_of_care_details: yup.string(),
                allowSigningOfNote: yup.bool(),
                anxiety: yup.bool(),
                panic_attacks: yup.bool(),
                depression: yup.bool(),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
                  supervisory_intake_statement:
                      noteDetails?.note_content?.supervisory_intake_statement ??
                      null,
                  medical_necessity_rationale:
                      noteDetails?.note_content?.medical_necessity_rationale ??
                      null,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    // Get frequency for select
    const FrequencyForSelect = ["daily", "weekly", "monthly", "yearly"].map(
        (item) => ({
            label: makeStringFirstLetterCapital(item),
            value: item,
        })
    );

    // Get relationship options for select
    const RelationshipForSelect = Object.values(ClientRelationship).map(
        (relationship) => ({
            label: removeEnumUnderscore(
                makeStringFirstLetterCapital(relationship)
            ),
            value: relationship,
        })
    );

    // Get gender options for select
    const GenderForSelect = Object.values(ClientGender).map((gender) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(gender)),
        value: gender,
    }));

    // Get religion options for select
    const ReligionForSelect = Object.values(ClientReligion).map((religion) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(religion)),
        value: religion,
    }));

    // Get education options for select
    const EducationForSelect = Object.values(ClientEducation).map(
        (education) => ({
            label: removeEnumUnderscore(
                makeStringFirstLetterCapital(education)
            ),
            value: education,
        })
    );

    // Get sexual orientation options for select
    const SexualOrientationForSelect = Object.values(
        ClientSexualOrientation
    ).map((orientation) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(orientation)),
        value: orientation,
    }));

    // Get education options for select
    const RaceForSelect = Object.values(ClientRace).map((race) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(race)),
        value: race,
    }));

    const anxiety = watch("anxiety", false);
    const panicAttacks = watch("panic_attacks", false);
    const depression = watch("depression", false);

    const supervisionPlan = watch("risk_review_supervision_plan");
    const shouldShowFormalSafetyPlan =
        Array.isArray(supervisionPlan) &&
        supervisionPlan.includes("Formal safety plan completed/updated");
    const shouldShowHigherLevelOfCare =
        Array.isArray(supervisionPlan) &&
        supervisionPlan.includes("Higher level of care considered/recommended");

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };
    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    // Get appointment location options
    const AppointmentLocationsForSelect = Object.values(
        AppointmentLocation
    ).map((location) => {
        return {
            label: removeEnumUnderscore(makeStringFirstLetterCapital(location)),
            value: location,
        };
    });

    const isCheckboxChecked = (checkboxValue: string, key: string) => {
        return Boolean(
            noteDetails.note_content?.[key]?.find(
                (value: string) => value === checkboxValue
            )
        );
    };

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "a_specific_plan_of_how_you_would_kill_yourself",
            note.note_content.a_specific_plan_of_how_you_would_kill_yourself
        );
        setValue(
            "access_to_weapons_or_means_of_hurting_self",
            note.note_content.access_to_weapons_or_means_of_hurting_self
        );
        setValue("anxiety", note.note_content.anxiety);
        setValue("anxiety_details", note.note_content.anxiety_details);
        setValue("anxiety_frequency", note.note_content.anxiety_frequency);
        setValue(
            "appointment_location",
            note.note_content.appointment_location
        );
        setValue(
            "been_arrested_or_detained_for_violent_behavior",
            note.note_content.been_arrested_or_detained_for_violent_behavior
        );
        setValue(
            "been_on_probation_for_any_reason",
            note.note_content.been_on_probation_for_any_reason
        );
        setValue(
            "been_so_distressed_you_seriously_wished_to_end_your_life",
            note.note_content
                .been_so_distressed_you_seriously_wished_to_end_your_life
        );
        setValue(
            "describe_been_so_distressed_you_seriously_wished_to_end_your_life",
            note.note_content
                .describe_been_so_distressed_you_seriously_wished_to_end_your_life
        );
        setValue(
            "been_to_jail_for_any_reason",
            note.note_content.been_to_jail_for_any_reason
        );
        setValue("children_history", note.note_content.children_history);
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue("current_employment", note.note_content.current_employment);
        setValue(
            "current_employment_details",
            note.note_content.current_employment_details
        );
        setValue("delivery_option", note.note_content.delivery_option);
        setValue("depression", note.note_content.depression);
        setValue("depression_details", note.note_content.depression_details);
        setValue(
            "depression_frequency",
            note.note_content.depression_frequency
        );
        setValue(
            "describe_done_something_to_hurt_yourself",
            note.note_content.describe_done_something_to_hurt_yourself
        );
        setValue(
            "describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live",
            note.note_content
                .describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
        );
        setValue(
            "describe_a_specific_plan_of_how_you_would_kill_yourself",
            note.note_content
                .describe_a_specific_plan_of_how_you_would_kill_yourself
        );
        setValue(
            "describe_access_to_weapons_or_means_of_hurting_self",
            note.note_content
                .describe_access_to_weapons_or_means_of_hurting_self
        );
        setValue(
            "details_for_diminished_poor_limited_quality_patient_oriented_evidence",
            note.note_content
                .details_for_diminished_poor_limited_quality_patient_oriented_evidence
        );
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue(
            "difficulties_with_appetite_or_eating_patterns",
            note.note_content.difficulties_with_appetite_or_eating_patterns
        );
        setValue(
            "do_you_have_access_to_guns",
            note.note_content.do_you_have_access_to_guns
        );
        setValue(
            "describe_do_you_have_access_to_guns",
            note.note_content.describe_do_you_have_access_to_guns
        );
        setValue(
            "describe_made_serious_suicide_attempts",
            note.note_content.describe_made_serious_suicide_attempts
        );
        setValue(
            "ever_run_away_from_home",
            note.note_content.ever_run_away_from_home
        );
        setValue(
            "describe_ever_run_away_from_home",
            note.note_content.describe_ever_run_away_from_home
        );
        setValue(
            "ever_been_bullied_or_bullied_someone",
            note.note_content.ever_been_bullied_or_bullied_someone
        );
        setValue(
            "describe_ever_been_bullied_or_bullied_someone",
            note.note_content.describe_ever_been_bullied_or_bullied_someone
        );
        setValue("duration", note.note_content.duration);
        setValue("education", note.note_content.education);
        setValue("gender", note.note_content.gender);
        setValue("preferred_name", note.note_content.preferred_name);
        setValue("other_gender", note.note_content.other_gender);
        setValue("pronouns", note.note_content.pronouns);
        setValue(
            "other_sexual_orientation",
            note.note_content.other_sexual_orientation
        );
        setValue(
            "grandparents_history",
            note.note_content.grandparents_history
        );
        setValue(
            "had_relatives_who_attempted_or_committed_suicide",
            note.note_content.had_relatives_who_attempted_or_committed_suicide
        );
        setValue(
            "describe_had_relatives_who_attempted_or_committed_suicide",
            note.note_content
                .describe_had_relatives_who_attempted_or_committed_suicide
        );
        setValue(
            "had_thoughts_of_killing_or_seriously_hurting_someone",
            note.note_content
                .had_thoughts_of_killing_or_seriously_hurting_someone
        );
        setValue(
            "describe_had_thoughts_of_killing_or_seriously_hurting_someone",
            note.note_content
                .describe_had_thoughts_of_killing_or_seriously_hurting_someone
        );
        setValue(
            "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live",
            note.note_content
                .has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
        );
        setValue(
            "heard_voices_telling_you_to_hurt_others",
            note.note_content.heard_voices_telling_you_to_hurt_others
        );
        setValue(
            "describe_heard_voices_telling_you_to_hurt_others",
            note.note_content.describe_heard_voices_telling_you_to_hurt_others
        );
        setValue(
            "heard_voices_telling_you_to_hurt_yourself",
            note.note_content.heard_voices_telling_you_to_hurt_yourself
        );
        setValue(
            "describe_heard_voices_telling_you_to_hurt_yourself",
            note.note_content.describe_heard_voices_telling_you_to_hurt_yourself
        );
        setValue(
            "hurt_someone_or_destroyed_property_on_purpose",
            note.note_content.hurt_someone_or_destroyed_property_on_purpose
        );
        setValue(
            "describe_hurt_someone_or_destroyed_property_on_purpose",
            note.note_content
                .describe_hurt_someone_or_destroyed_property_on_purpose
        );
        setValue(
            "impatient_psychiatric_hospitalization",
            note.note_content.impatient_psychiatric_hospitalization
        );
        setValue("legal_problems", note.note_content.legal_problems);
        setValue(
            "made_serious_suicide_attempts",
            note.note_content.made_serious_suicide_attempts
        );
        setValue(
            "medication_physical_psychiatric",
            note.note_content.medication_physical_psychiatric
        );
        setValue(
            "mental_health_affect_mood",
            note.note_content.mental_health_affect_mood
        );
        setValue(
            "mental_health_appearance",
            note.note_content.mental_health_appearance
        );
        setValue(
            "mental_health_attention_concentration_effort",
            note.note_content.mental_health_attention_concentration_effort
        );
        setValue(
            "mental_health_cognition",
            note.note_content.mental_health_cognition
        );
        setValue(
            "mental_health_movement",
            note.note_content.mental_health_movement
        );
        setValue(
            "mental_health_presentation_of_self",
            note.note_content.mental_health_presentation_of_self
        );
        setValue(
            "mental_health_speech_behaviour",
            note.note_content.mental_health_speech_behaviour
        );
        setValue("name_of_partner", note.note_content.name_of_partner);
        setValue(
            "other_delivery_option",
            note.note_content.other_delivery_option
        );
        setValue(
            "other_family_members_history",
            note.note_content.other_family_members_history
        );
        setValue(
            "other_person_present_at_session",
            note.note_content.other_person_present_at_session
        );
        setValue(
            "other_provider_location",
            note.note_content.other_provider_location
        );
        setValue("other_symptoms", note.note_content.other_symptoms);
        setValue(
            "family_members_alcohol_abuse",
            note.note_content.family_members_alcohol_abuse
        );
        setValue(
            "family_members_alcohol_abuse_other",
            note.note_content.family_members_alcohol_abuse_other
        );
        setValue(
            "family_members_anxiety",
            note.note_content.family_members_anxiety
        );
        setValue(
            "family_members_anxiety_other",
            note.note_content.family_members_anxiety_other
        );
        setValue(
            "family_members_bipolar_disorder",
            note.note_content.family_members_bipolar_disorder
        );
        setValue(
            "family_members_bipolar_disorder_other",
            note.note_content.family_members_bipolar_disorder_other
        );
        setValue(
            "family_members_depression",
            note.note_content.family_members_depression
        );
        setValue(
            "family_members_depression_other",
            note.note_content.family_members_depression_other
        );
        setValue("family_members_ptsd", note.note_content.family_members_ptsd);
        setValue(
            "family_members_ptsd_other",
            note.note_content.family_members_ptsd_other
        );
        setValue(
            "family_members_schizophrenia",
            note.note_content.family_members_schizophrenia
        );
        setValue(
            "family_members_schizophrenia_other",
            note.note_content.family_members_schizophrenia_other
        );
        setValue(
            "family_members_substance_abuse_other",
            note.note_content.family_members_substance_abuse_other
        );
        setValue(
            "family_members_substance_abuse",
            note.note_content.family_members_substance_abuse
        );
        setValue(
            "family_members_substance_abuse_substance",
            note.note_content.family_members_substance_abuse_substance
        );

        setValue(
            "other_treatment_modality_intervention",
            note.note_content.other_treatment_modality_intervention
        );
        setValue("panic_attacks", note.note_content.panic_attacks);
        setValue(
            "panic_attacks_details",
            note.note_content.panic_attacks_details
        );
        setValue(
            "panic_attacks_frequency",
            note.note_content.panic_attacks_frequency
        );
        setValue(
            "parents_stepparents_history",
            note.note_content.parents_stepparents_history
        );
        setValue(
            "patients_ability_and_capability_to_respond_to_treatment",
            note.note_content
                .patients_ability_and_capability_to_respond_to_treatment
        );
        setValue(
            "people_present_at_session",
            note.note_content.people_present_at_session
        );
        setValue("physical_symptoms", note.note_content.physical_symptoms);
        setValue(
            "previous_mental_health_treatment_oupatient",
            note.note_content.previous_mental_health_treatment_oupatient
        );
        setValue("prognosis", note.note_content.prognosis);
        setValue("provider_location", note.note_content.provider_location);
        setValue(
            "purposely_done_something_to_hurt_yourself",
            note.note_content.purposely_done_something_to_hurt_yourself
        );
        setValue("race", note.note_content.race);
        setValue("relationship_status", note.note_content.relationship_status);
        setValue(
            "relevant_medical_history",
            note.note_content.relevant_medical_history
        );
        setValue("religion", note.note_content.religion);
        setValue("selected_symptoms", note.note_content.selected_symptoms);
        setValue("sexual_orientation", note.note_content.sexual_orientation);
        setValue("siblings_history", note.note_content.siblings_history);
        setValue(
            "slapped_kicked_punched_someone_with_intent_to_harm",
            note.note_content.slapped_kicked_punched_someone_with_intent_to_harm
        );
        setValue(
            "describe_slapped_kicked_punched_someone_with_intent_to_harm",
            note.note_content
                .describe_slapped_kicked_punched_someone_with_intent_to_harm
        );
        setValue(
            "sleeping_habits_and_concerns",
            note.note_content.sleeping_habits_and_concerns
        );
        setValue("subjective_report", note.note_content.subjective_report);
        setValue("summary", note.note_content.summary);
        setValue(
            "the_teletherapy_session_was_conducted_via_video",
            note.note_content.the_teletherapy_session_was_conducted_via_video
        );
        setValue(
            "trauma_abuse_history",
            note.note_content.trauma_abuse_history
        );
        setValue("treatment_goal_one", note.note_content.treatment_goal_one);
        setValue(
            "treatment_goal_three",
            note.note_content.treatment_goal_three
        );
        setValue("treatment_goal_two", note.note_content.treatment_goal_two);
        setValue(
            "frequency_of_sessions",
            note.note_content.frequency_of_sessions
        );
        setValue(
            "other_frequency_of_sessions",
            note.note_content.other_frequency_of_sessions
        );
        setValue(
            "treatment_modality_intervention",
            note.note_content.treatment_modality_intervention
        );
        setValue(
            "type_of_treatment_recommended",
            note.note_content.type_of_treatment_recommended
        );
        setValue(
            "what_are_the_problem_you_are_seeking_help_for",
            note.note_content.what_are_the_problem_you_are_seeking_help_for
        );
        setValue(
            "contact_information_for_both_parents",
            note.note_content.contact_information_for_both_parents
        );
        setValue(
            "name_address_of_personal_physician",
            note.note_content.name_address_of_personal_physician
        );
        setValue("date_of_last_exam", note.note_content.date_of_last_exam);
        setValue(
            "supervisory_intake_statement",
            note.note_content.supervisory_intake_statement
        );
        setValue(
            "describe_been_arrested_or_detained_for_violent_behavior",
            note.note_content
                .describe_been_arrested_or_detained_for_violent_behavior
        );
        setValue(
            "describe_been_to_jail_for_any_reason",
            note.note_content.describe_been_to_jail_for_any_reason
        );
        setValue(
            "describe_been_on_probation_for_any_reason",
            note.note_content.describe_been_on_probation_for_any_reason
        );
        setValue(
            "medical_necessity_rationale",
            note.note_content.medical_necessity_rationale
        );
        setValue("risk_review_supervision_plan", [
            note.note_content.risk_review_supervision_plan,
        ]);
        setValue(
            "formal_safety_plan_details",
            note.note_content.formal_safety_plan_details
        );
        setValue(
            "higher_level_of_care_details",
            note.note_content.higher_level_of_care_details
        );
        setShowPrompt(false);
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <form
                aria-label="edit individual intake"
                id="edit-note-form"
                onSubmit={(event) => {
                    isSigning.current = true;
                    handleSubmit(handleSignNote)(event);
                }}
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <section id="step-one" className="mb-10">
                    <h3 className="note-label note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.supervisory_intake_statement
                        }
                    </h3>
                    <div className="note-checkboxes">
                        <Controller
                            name="supervisory_intake_statement"
                            control={control}
                            render={({ field }) => {
                                const checkboxValue =
                                    "This diagnostic intake was completed by a licensed clinician, which helped determine diagnosis, medical necessity, and initial treatment recommendations";
                                return (
                                    <FormCheckbox
                                        label={checkboxValue}
                                        value={checkboxValue}
                                        checked={
                                            field.value?.includes(
                                                checkboxValue
                                            ) ?? false
                                        }
                                        onChange={(e) =>
                                            field.onChange(
                                                e.target.checked
                                                    ? [checkboxValue]
                                                    : null
                                            )
                                        }
                                    />
                                );
                            }}
                        />
                    </div>
                    {errors.supervisory_intake_statement ? (
                        <div className="note-error">
                            <p>Supervisory intake statement is required</p>
                        </div>
                    ) : null}
                    <div className="fg">
                        <Controller
                            name="appointment_location"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    isRequired
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.appointment_location
                                    }
                                    placeholder=""
                                    onChange={(val) =>
                                        field.onChange((val as Option).value)
                                    }
                                    value={
                                        AppointmentLocationsForSelect.find(
                                            (location) =>
                                                location.value === field.value
                                        ) || null
                                    }
                                    options={AppointmentLocationsForSelect}
                                    hasError={!!errors.appointment_location}
                                    errorText={
                                        errors.appointment_location?.message
                                    }
                                />
                            )}
                        />
                    </div>
                    {watch(`appointment_location`) ===
                        AppointmentLocation.TELETHERAPY && (
                        <>
                            <p className="note-label">
                                Client Teletherapy Location
                            </p>
                            <div className="fg fg-space-between three flex">
                                <RadioInput
                                    {...register(`delivery_option`)}
                                    label="Home"
                                    value="Home"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.delivery_option === "Home"
                                    }
                                />

                                <RadioInput
                                    {...register(`delivery_option`)}
                                    label="Office"
                                    value="Office"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.delivery_option === "Office"
                                    }
                                />

                                <RadioInput
                                    {...register(`delivery_option`)}
                                    label="Other"
                                    value="Other"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.delivery_option === "Other"
                                    }
                                />
                            </div>
                            {errors.delivery_option ? (
                                <div className="note-error">
                                    <p>Delivery option is required</p>
                                </div>
                            ) : null}
                            {watch(`delivery_option`) === "Other" ||
                            noteDetails.note_content?.delivery_option ===
                                "Other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(`other_delivery_option`)}
                                        label={
                                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_delivery_option
                                        }
                                        hasError={
                                            !!errors?.other_delivery_option
                                        }
                                        errorText={
                                            errors?.other_delivery_option
                                                ?.message
                                        }
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.other_delivery_option
                                        }
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                    {watch(`appointment_location`) ===
                        AppointmentLocation.TELETHERAPY && (
                        <>
                            <p className="note-label">
                                {
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.provider_location
                                }
                            </p>
                            <div className="fg fg-space-between three flex">
                                <RadioInput
                                    {...register(`provider_location`)}
                                    label="Home"
                                    value="Home"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.provider_location === "Home"
                                    }
                                />

                                <RadioInput
                                    {...register(`provider_location`)}
                                    label="Office"
                                    value="Office"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.provider_location === "Office"
                                    }
                                />

                                <RadioInput
                                    {...register(`provider_location`)}
                                    label="Other"
                                    value="Other"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.provider_location === "Other"
                                    }
                                />
                            </div>
                            {errors.provider_location ? (
                                <div className="note-error">
                                    <p>Delivery option is required</p>
                                </div>
                            ) : null}
                            {watch(`provider_location`) === "Other" ||
                            noteDetails.note_content?.provider_location ===
                                "Other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(`other_provider_location`)}
                                        label={
                                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_provider_location
                                        }
                                        hasError={
                                            !!errors?.other_provider_location
                                        }
                                        errorText={
                                            errors?.other_provider_location
                                                ?.message
                                        }
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.other_provider_location
                                        }
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                    {watch("appointment_location") ===
                    AppointmentLocation.TELETHERAPY ? (
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_teletherapy_session_was_conducted_via_video"
                                )}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.the_teletherapy_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_teletherapy_session_was_conducted_via_video
                                }
                            />
                        </div>
                    ) : null}

                    <div className="fg">
                        <Controller
                            name="diagnosis_codes"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.diagnosis_codes
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    isRequired
                                    hideSelectedOptions
                                    errorText={
                                        errors.diagnosis_codes?.type ===
                                        "typeError"
                                            ? "Diagnosis codes are required"
                                            : errors.diagnosis_codes?.message
                                    }
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="secondary_diagnosis"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.secondary_diagnosis
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.secondary_diagnosis?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="tertiary_diagnosis"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.tertiary_diagnosis
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.tertiary_diagnosis?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="additional_diagnosis_one"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.additional_diagnosis_one
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.additional_diagnosis_one?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="additional_diagnosis_two"
                            control={control}
                            render={({ field }) => (
                                <DiagnosisCodeSelect
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.additional_diagnosis_two
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    errorText={
                                        errors.additional_diagnosis_two?.message
                                    }
                                    isOptional
                                    isMulti
                                />
                            )}
                        />
                    </div>
                    <h3 className="note-header">Identity</h3>
                    <div className="fg">
                        <Input
                            {...register("preferred_name")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.preferred_name
                            }
                            defaultValue={
                                noteDetails.note_content?.preferred_name
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="gender"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.gender
                                    }
                                    placeholder=""
                                    value={
                                        GenderForSelect.find(
                                            (gender) =>
                                                gender.value === field.value
                                        ) || null
                                    }
                                    onChange={(val) =>
                                        field.onChange((val as Option).value)
                                    }
                                    options={GenderForSelect}
                                    isRequired
                                    hasError={!!errors.gender}
                                    errorText={errors.gender?.message}
                                />
                            )}
                        />
                    </div>
                    {watch("gender") === "other" ? (
                        <div className="fg">
                            <Input
                                {...register("other_gender")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_gender
                                }
                                defaultValue={
                                    noteDetails.note_content?.other_gender
                                }
                                isRequired
                                hasError={!!errors.other_gender}
                                errorText={errors.other_gender?.message}
                            />
                        </div>
                    ) : null}
                    <div className="fg">
                        <Input
                            {...register("pronouns")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.pronouns
                            }
                            defaultValue={noteDetails.note_content?.pronouns}
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="religion"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.religion
                                    }
                                    placeholder=""
                                    value={
                                        ReligionForSelect.find(
                                            (religion) =>
                                                religion.value === field.value
                                        ) || null
                                    }
                                    onChange={(val) => {
                                        if (val === null) {
                                            field.onChange("");
                                            return;
                                        }
                                        field.onChange((val as Option).value);
                                    }}
                                    options={ReligionForSelect}
                                    isOptional
                                    isClearable
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="education"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.education
                                    }
                                    placeholder=""
                                    value={
                                        EducationForSelect.find(
                                            (education) =>
                                                education.value === field.value
                                        ) || null
                                    }
                                    onChange={(val) => {
                                        if (val === null) {
                                            field.onChange("");
                                            return;
                                        }
                                        field.onChange((val as Option).value);
                                    }}
                                    options={EducationForSelect}
                                    isOptional
                                    isClearable
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="race"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.race
                                    }
                                    placeholder=""
                                    value={
                                        RaceForSelect.find(
                                            (race) => race.value === field.value
                                        ) || null
                                    }
                                    onChange={(val) => {
                                        if (val === null) {
                                            field.onChange("");
                                            return;
                                        }
                                        field.onChange((val as Option).value);
                                    }}
                                    options={RaceForSelect}
                                    isOptional
                                    isClearable
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("ethnicity")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.ethinicity
                            }
                            placeholder={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.ethinicity
                            }
                            defaultValue={noteDetails.note_content?.ethinicity}
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="sexual_orientation"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.sexual_orientation
                                    }
                                    placeholder=""
                                    value={
                                        SexualOrientationForSelect.find(
                                            (orientation) =>
                                                orientation.value ===
                                                field.value
                                        ) || null
                                    }
                                    onChange={(val) => {
                                        if (val === null) {
                                            field.onChange("");
                                            return;
                                        }
                                        field.onChange((val as Option).value);
                                    }}
                                    options={SexualOrientationForSelect}
                                    isOptional
                                    isClearable
                                />
                            )}
                        />
                    </div>
                    {watch("sexual_orientation") === "other" ? (
                        <div className="fg">
                            <Input
                                {...register("other_sexual_orientation")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_sexual_orientation
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.other_sexual_orientation
                                }
                                isRequired
                                hasError={!!errors.other_sexual_orientation}
                                errorText={
                                    errors.other_sexual_orientation?.message
                                }
                            />
                        </div>
                    ) : null}
                    <div className="fg">
                        <Input
                            {...register("current_employment")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.current_employment
                            }
                            defaultValue={
                                noteDetails.note_content?.current_employment
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.current_employment_details
                            }
                            {...register("current_employment_details")}
                            defaultValue={
                                noteDetails.note_content
                                    ?.current_employment_details
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <Controller
                            name="relationship_status"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.relationship_status
                                    }
                                    placeholder=""
                                    value={
                                        RelationshipForSelect.find(
                                            (relationship) =>
                                                relationship.value ===
                                                field.value
                                        ) || null
                                    }
                                    onChange={(val) => {
                                        if (val === null) {
                                            field.onChange("");
                                            return;
                                        }
                                        field.onChange((val as Option).value);
                                    }}
                                    options={RelationshipForSelect}
                                    isOptional
                                    isClearable
                                />
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("name_of_partner")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.name_of_partner
                            }
                            placeholder={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.name_of_partner
                            }
                            defaultValue={
                                noteDetails.note_content?.name_of_partner
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("emergency_contact_information")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.emergency_contact_information
                            }
                            placeholder={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.emergency_contact_information
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.emergency_contact_information
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("legal_problems")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.legal_problems
                            }
                            placeholder={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.legal_problems
                            }
                            defaultValue={
                                noteDetails.note_content?.legal_problems
                            }
                            isOptional
                        />
                    </div>
                </section>
                <section id="step-two" className="mb-10">
                    <h3 className="note-label-req">Presenting Concerns</h3>
                    <p className="note-label">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.what_are_the_problem_you_are_seeking_help_for
                        }
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register(
                                "what_are_the_problem_you_are_seeking_help_for"
                            )}
                            defaultValue={
                                noteDetails.note_content
                                    ?.what_are_the_problem_you_are_seeking_help_for
                            }
                            hasError={
                                !!errors.what_are_the_problem_you_are_seeking_help_for
                            }
                            errorText={
                                errors
                                    .what_are_the_problem_you_are_seeking_help_for
                                    ?.message
                            }
                        />
                    </div>
                    <p className="note-label note-label-opt">Symptoms</p>
                    <div className="mb-[--spacing-form-items]">
                        <FormCheckbox
                            {...register("anxiety")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.anxiety
                            }
                            defaultChecked={noteDetails.note_content?.anxiety}
                        />
                    </div>
                    {anxiety && (
                        <div className="fg fg-space-between two flex">
                            <Controller
                                name="anxiety_frequency"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label={
                                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.anxiety_frequency
                                        }
                                        placeholder=""
                                        onChange={(val) => {
                                            if (val === null) {
                                                field.onChange("");
                                                return;
                                            }
                                            field.onChange(
                                                (val as Option).value
                                            );
                                        }}
                                        value={
                                            FrequencyForSelect.find(
                                                (frequency) =>
                                                    frequency.value ===
                                                    field.value
                                            ) || null
                                        }
                                        options={FrequencyForSelect}
                                        isClearable
                                    />
                                )}
                            />
                            <TextArea
                                {...register("anxiety_details")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.anxiety_details
                                }
                                defaultValue={
                                    noteDetails.note_content?.anxiety_details
                                }
                                hasError={!!errors.anxiety_details}
                                errorText={errors.anxiety_details?.message}
                                isRequired
                            />
                        </div>
                    )}
                    <div className="mb-[--spacing-form-items]">
                        <FormCheckbox
                            {...register("panic_attacks")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.panic_attacks
                            }
                            defaultChecked={
                                noteDetails.note_content?.panic_attacks
                            }
                        />
                    </div>
                    {panicAttacks && (
                        <div className="fg fg-space-between two flex">
                            <Controller
                                name="panic_attacks_frequency"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label={
                                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.panic_attacks_frequency
                                        }
                                        placeholder=""
                                        onChange={(val) => {
                                            if (val === null) {
                                                field.onChange("");
                                                return;
                                            }
                                            field.onChange(
                                                (val as Option).value
                                            );
                                        }}
                                        value={
                                            FrequencyForSelect.find(
                                                (frequency) =>
                                                    frequency.value ===
                                                    field.value
                                            ) || null
                                        }
                                        options={FrequencyForSelect}
                                        isClearable
                                    />
                                )}
                            />
                            <TextArea
                                {...register("panic_attacks_details")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.panic_attacks_details
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.panic_attacks_details
                                }
                                hasError={!!errors.panic_attacks_details}
                                errorText={
                                    errors.panic_attacks_details?.message
                                }
                                isRequired
                            />
                        </div>
                    )}
                    <div className="mb-[--spacing-form-items]">
                        <FormCheckbox
                            {...register("depression")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.depression
                            }
                            defaultChecked={
                                noteDetails.note_content?.depression
                            }
                        />
                    </div>
                    {depression && (
                        <div className="fg fg-space-between two flex">
                            <Controller
                                name="depression_frequency"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        label={
                                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.depression_frequency
                                        }
                                        placeholder=""
                                        onChange={(val) => {
                                            if (val === null) {
                                                field.onChange("");
                                                return;
                                            }
                                            field.onChange(
                                                (val as Option).value
                                            );
                                        }}
                                        value={
                                            FrequencyForSelect.find(
                                                (frequency) =>
                                                    frequency.value ===
                                                    field.value
                                            ) || null
                                        }
                                        options={FrequencyForSelect}
                                        isClearable
                                    />
                                )}
                            />
                            <TextArea
                                {...register("depression_details")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.depression_details
                                }
                                defaultValue={
                                    noteDetails.note_content?.depression_details
                                }
                                hasError={!!errors.depression_details}
                                errorText={errors.depression_details?.message}
                                isRequired
                            />
                        </div>
                    )}
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Easily distracted"
                            value="Easily distracted"
                            defaultChecked={isCheckboxChecked(
                                "Easily distracted",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Impulsive"
                            value="Impulsive"
                            defaultChecked={isCheckboxChecked(
                                "Impulsive",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Paranoia"
                            value="Paranoia"
                            defaultChecked={isCheckboxChecked(
                                "Paranoia",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Alcohol"
                            value="Alcohol"
                            defaultChecked={isCheckboxChecked(
                                "Alcohol",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Anger outburst"
                            value="Anger outburst"
                            defaultChecked={isCheckboxChecked(
                                "Anger outburst",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Unable to feel pleasure"
                            value="Unable to feel pleasure"
                            defaultChecked={isCheckboxChecked(
                                "Unable to feel pleasure",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Excessive energy"
                            value="Excessive energy"
                            defaultChecked={isCheckboxChecked(
                                "Excessive energy",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Recreational drug use"
                            value="Recreational drug use"
                            defaultChecked={isCheckboxChecked(
                                "Recreational drug use",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Tobacco"
                            value="Tobacco"
                            defaultChecked={isCheckboxChecked(
                                "Tobacco",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                        <FormCheckbox
                            {...register("selected_symptoms")}
                            label="Racing thoughts"
                            value="Racing thoughts"
                            defaultChecked={isCheckboxChecked(
                                "Racing thoughts",
                                noteDetails.note_content?.selected_symptoms
                            )}
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("other_symptoms")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_symptoms
                            }
                            defaultValue={
                                noteDetails.note_content?.other_symptoms
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("physical_symptoms")}
                            defaultValue={
                                noteDetails.note_content?.physical_symptoms
                            }
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.physical_symptoms
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("medication_physical_psychiatric")}
                            defaultValue={
                                noteDetails.note_content
                                    ?.medication_physical_psychiatric
                            }
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.medication_physical_psychiatric
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("sleeping_habits_and_concerns")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.sleeping_habits_and_concerns
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.sleeping_habits_and_concerns
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register(
                                "difficulties_with_appetite_or_eating_patterns"
                            )}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.difficulties_with_appetite_or_eating_patterns
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.difficulties_with_appetite_or_eating_patterns
                            }
                            isOptional
                        />
                    </div>
                </section>
                <section id="step-three" className="mb-10">
                    <h3 className="note-header">Family history</h3>
                    <p className="note-label">Describe family situation</p>
                    <div className="fg">
                        <TextArea
                            {...register("parents_stepparents_history")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.parents_stepparents_history
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.parents_stepparents_history
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("siblings_history")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.siblings_history
                            }
                            defaultValue={
                                noteDetails.note_content?.siblings_history
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("grandparents_history")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.grandparents_history
                            }
                            defaultValue={
                                noteDetails.note_content?.grandparents_history
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("children_history")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.children_history
                            }
                            defaultValue={
                                noteDetails.note_content?.children_history
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("other_family_members_history")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_family_members_history
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_family_members_history
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-header">Family Psychiatric History</h3>
                    <p className="note-label">
                        Select which family members the below situation apply to
                    </p>
                    <h3 className="note-label note-label-opt">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_alcohol_abuse
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("family_members_alcohol_abuse")}
                            label="All"
                            value="All"
                            defaultChecked={isCheckboxChecked(
                                "All",
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_alcohol_abuse")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_alcohol_abuse")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_alcohol_abuse")}
                            label="Child/Children"
                            value="Child/Children"
                            defaultChecked={isCheckboxChecked(
                                "Child/Children",
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_alcohol_abuse")}
                            label="Brother"
                            value="Brother"
                            defaultChecked={isCheckboxChecked(
                                "Brother",
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_alcohol_abuse")}
                            label="Sister"
                            value="Sister"
                            defaultChecked={isCheckboxChecked(
                                "Sister",
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_alcohol_abuse")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("family_members_alcohol_abuse_other")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_alcohol_abuse_other
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_alcohol_abuse_other
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label note-label-opt">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_anxiety
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("family_members_anxiety")}
                            label="All"
                            value="All"
                            defaultChecked={isCheckboxChecked(
                                "All",
                                noteDetails.note_content?.family_members_anxiety
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_anxiety")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content?.family_members_anxiety
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_anxiety")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content?.family_members_anxiety
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_anxiety")}
                            label="Child/Children"
                            value="Child/Children"
                            defaultChecked={isCheckboxChecked(
                                "Child/Children",
                                noteDetails.note_content?.family_members_anxiety
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_anxiety")}
                            label="Brother"
                            value="Brother"
                            defaultChecked={isCheckboxChecked(
                                "Brother",
                                noteDetails.note_content?.family_members_anxiety
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_anxiety")}
                            label="Sister"
                            value="Sister"
                            defaultChecked={isCheckboxChecked(
                                "Sister",
                                noteDetails.note_content?.family_members_anxiety
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_anxiety")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content?.family_members_anxiety
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("family_members_anxiety_other")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_anxiety_other
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_anxiety_other
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label note-label-opt">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_bipolar_disorder
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("family_members_bipolar_disorder")}
                            label="All"
                            value="All"
                            defaultChecked={isCheckboxChecked(
                                "All",
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_bipolar_disorder")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_bipolar_disorder")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_bipolar_disorder")}
                            label="Child/Children"
                            value="Child/Children"
                            defaultChecked={isCheckboxChecked(
                                "Child/Children",
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_bipolar_disorder")}
                            label="Brother"
                            value="Brother"
                            defaultChecked={isCheckboxChecked(
                                "Brother",
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_bipolar_disorder")}
                            label="Sister"
                            value="Sister"
                            defaultChecked={isCheckboxChecked(
                                "Sister",
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_bipolar_disorder")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register(
                                "family_members_bipolar_disorder_other"
                            )}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_bipolar_disorder_other
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_bipolar_disorder_other
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label note-label-opt">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_depression
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("family_members_depression")}
                            label="All"
                            value="All"
                            defaultChecked={isCheckboxChecked(
                                "All",
                                noteDetails.note_content
                                    ?.family_members_depression
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_depression")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.family_members_depression
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_depression")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.family_members_depression
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_depression")}
                            label="Child/Children"
                            value="Child/Children"
                            defaultChecked={isCheckboxChecked(
                                "Child/Children",
                                noteDetails.note_content
                                    ?.family_members_depression
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_depression")}
                            label="Brother"
                            value="Brother"
                            defaultChecked={isCheckboxChecked(
                                "Brother",
                                noteDetails.note_content
                                    ?.family_members_depression
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_depression")}
                            label="Sister"
                            value="Sister"
                            defaultChecked={isCheckboxChecked(
                                "Sister",
                                noteDetails.note_content
                                    ?.family_members_depression
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_depression")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.family_members_depression
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("family_members_depression_other")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_depression_other
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_depression_other
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label note-label-opt">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_ptsd
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("family_members_ptsd")}
                            label="All"
                            value="All"
                            defaultChecked={isCheckboxChecked(
                                "All",
                                noteDetails.note_content?.family_members_ptsd
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_ptsd")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content?.family_members_ptsd
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_ptsd")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content?.family_members_ptsd
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_ptsd")}
                            label="Child/Children"
                            value="Child/Children"
                            defaultChecked={isCheckboxChecked(
                                "Child/Children",
                                noteDetails.note_content?.family_members_ptsd
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_ptsd")}
                            label="Brother"
                            value="Brother"
                            defaultChecked={isCheckboxChecked(
                                "Brother",
                                noteDetails.note_content?.family_members_ptsd
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_ptsd")}
                            label="Sister"
                            value="Sister"
                            defaultChecked={isCheckboxChecked(
                                "Sister",
                                noteDetails.note_content?.family_members_ptsd
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_ptsd")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content?.family_members_ptsd
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("family_members_ptsd_other")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_ptsd_other
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_ptsd_other
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label note-label-opt">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_schizophrenia
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("family_members_schizophrenia")}
                            label="All"
                            value="All"
                            defaultChecked={isCheckboxChecked(
                                "All",
                                noteDetails.note_content
                                    ?.family_members_schizophrenia
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_schizophrenia")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.family_members_schizophrenia
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_schizophrenia")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.family_members_schizophrenia
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_schizophrenia")}
                            label="Child/Children"
                            value="Child/Children"
                            defaultChecked={isCheckboxChecked(
                                "Child/Children",
                                noteDetails.note_content
                                    ?.family_members_schizophrenia
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_schizophrenia")}
                            label="Brother"
                            value="Brother"
                            defaultChecked={isCheckboxChecked(
                                "Brother",
                                noteDetails.note_content
                                    ?.family_members_schizophrenia
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_schizophrenia")}
                            label="Sister"
                            value="Sister"
                            defaultChecked={isCheckboxChecked(
                                "Sister",
                                noteDetails.note_content
                                    ?.family_members_schizophrenia
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_schizophrenia")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.family_members_schizophrenia
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register("family_members_schizophrenia_other")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_schizophrenia_other
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_schizophrenia_other
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label note-label-opt">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_substance_abuse
                        }
                    </h3>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("family_members_substance_abuse")}
                            label="All"
                            value="All"
                            defaultChecked={isCheckboxChecked(
                                "All",
                                noteDetails.note_content
                                    ?.family_members_substance_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_substance_abuse")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.family_members_substance_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_substance_abuse")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.family_members_substance_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_substance_abuse")}
                            label="Child/Children"
                            value="Child/Children"
                            defaultChecked={isCheckboxChecked(
                                "Child/Children",
                                noteDetails.note_content
                                    ?.family_members_substance_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_substance_abuse")}
                            label="Brother"
                            value="Brother"
                            defaultChecked={isCheckboxChecked(
                                "Brother",
                                noteDetails.note_content
                                    ?.family_members_substance_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_substance_abuse")}
                            label="Sister"
                            value="Sister"
                            defaultChecked={isCheckboxChecked(
                                "Sister",
                                noteDetails.note_content
                                    ?.family_members_substance_abuse
                            )}
                        />
                        <FormCheckbox
                            {...register("family_members_substance_abuse")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.family_members_substance_abuse
                            )}
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register(
                                "family_members_substance_abuse_other"
                            )}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_substance_abuse_other
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_substance_abuse_other
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <Input
                            {...register(
                                "family_members_substance_abuse_substance"
                            )}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.family_members_substance_abuse_substance
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.family_members_substance_abuse_substance
                            }
                            isOptional
                        />
                    </div>
                </section>
                <section id="step-four" className="mb-10">
                    <h3 className="note-header">Mental health history</h3>
                    <div className="fg">
                        <TextArea
                            {...register("trauma_abuse_history")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.trauma_abuse_history
                            }
                            defaultValue={
                                noteDetails.note_content?.trauma_abuse_history
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register(
                                "previous_mental_health_treatment_oupatient"
                            )}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.previous_mental_health_treatment_oupatient
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.previous_mental_health_treatment_oupatient
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register(
                                "impatient_psychiatric_hospitalization"
                            )}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.impatient_psychiatric_hospitalization
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.impatient_psychiatric_hospitalization
                            }
                            isOptional
                        />
                    </div>
                    <div className="fg">
                        <TextArea
                            {...register("relevant_medical_history")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.relevant_medical_history
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.relevant_medical_history
                            }
                            isOptional
                        />
                    </div>
                </section>
                <section id="step-five" className="mb-10">
                    <p className="note-header">Suicide risk assessment</p>
                    <p className="note-label-req">
                        {`1. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.been_so_distressed_you_seriously_wished_to_end_your_life
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.been_so_distressed_you_seriously_wished_to_end_your_life
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "been_so_distressed_you_seriously_wished_to_end_your_life"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_so_distressed_you_seriously_wished_to_end_your_life ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "been_so_distressed_you_seriously_wished_to_end_your_life"
                            )}
                            label="Today"
                            value="Today"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_so_distressed_you_seriously_wished_to_end_your_life ===
                                "Today"
                            }
                        />
                        <RadioInput
                            {...register(
                                "been_so_distressed_you_seriously_wished_to_end_your_life"
                            )}
                            label="Recently"
                            value="Recently"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_so_distressed_you_seriously_wished_to_end_your_life ===
                                "Recently"
                            }
                        />
                    </div>
                    {errors.been_so_distressed_you_seriously_wished_to_end_your_life ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch(
                        "been_so_distressed_you_seriously_wished_to_end_your_life"
                    ) === "Today" ||
                    watch(
                        "been_so_distressed_you_seriously_wished_to_end_your_life"
                    ) === "Recently" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_been_so_distressed_you_seriously_wished_to_end_your_life"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                }
                                hasError={
                                    !!errors.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                }
                                errorText={
                                    errors
                                        .describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`2. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch(
                        "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live"
                    ) === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                }
                                errorText={
                                    errors
                                        .describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                        ?.message
                                }
                                hasError={
                                    !!errors.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-header">Have you had/Do you have -</p>
                    <p className="note-label-req">
                        {`3. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.a_specific_plan_of_how_you_would_kill_yourself
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.a_specific_plan_of_how_you_would_kill_yourself
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "a_specific_plan_of_how_you_would_kill_yourself"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.a_specific_plan_of_how_you_would_kill_yourself ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "a_specific_plan_of_how_you_would_kill_yourself"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.a_specific_plan_of_how_you_would_kill_yourself ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.a_specific_plan_of_how_you_would_kill_yourself ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("a_specific_plan_of_how_you_would_kill_yourself") ===
                    "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_a_specific_plan_of_how_you_would_kill_yourself"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_a_specific_plan_of_how_you_would_kill_yourself
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_a_specific_plan_of_how_you_would_kill_yourself
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_a_specific_plan_of_how_you_would_kill_yourself
                                }
                                errorText={
                                    errors
                                        .describe_a_specific_plan_of_how_you_would_kill_yourself
                                        ?.message
                                }
                                hasError={
                                    !!errors.describe_a_specific_plan_of_how_you_would_kill_yourself
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`4. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.access_to_weapons_or_means_of_hurting_self
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.access_to_weapons_or_means_of_hurting_self
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "access_to_weapons_or_means_of_hurting_self"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.access_to_weapons_or_means_of_hurting_self ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "access_to_weapons_or_means_of_hurting_self"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.access_to_weapons_or_means_of_hurting_self ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.access_to_weapons_or_means_of_hurting_self ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("access_to_weapons_or_means_of_hurting_self") ===
                    "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_access_to_weapons_or_means_of_hurting_self"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_access_to_weapons_or_means_of_hurting_self
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_access_to_weapons_or_means_of_hurting_self
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_access_to_weapons_or_means_of_hurting_self
                                }
                                errorText={
                                    errors
                                        .describe_access_to_weapons_or_means_of_hurting_self
                                        ?.message
                                }
                                hasError={
                                    !!errors.describe_access_to_weapons_or_means_of_hurting_self
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`5. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.made_serious_suicide_attempts
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.made_serious_suicide_attempts
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register("made_serious_suicide_attempts")}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.made_serious_suicide_attempts === "No"
                            }
                        />
                        <RadioInput
                            {...register("made_serious_suicide_attempts")}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.made_serious_suicide_attempts === "Yes"
                            }
                        />
                    </div>
                    {errors.made_serious_suicide_attempts ? (
                        <p className="note-error">This field is required</p>
                    ) : null}
                    {watch("made_serious_suicide_attempts") === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_made_serious_suicide_attempts"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_made_serious_suicide_attempts
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_made_serious_suicide_attempts
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_made_serious_suicide_attempts
                                }
                                hasError={
                                    !!errors.describe_made_serious_suicide_attempts
                                }
                                errorText={
                                    errors
                                        .describe_made_serious_suicide_attempts
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`6. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.purposely_done_something_to_hurt_yourself
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.purposely_done_something_to_hurt_yourself
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "purposely_done_something_to_hurt_yourself"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.purposely_done_something_to_hurt_yourself ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "purposely_done_something_to_hurt_yourself"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.purposely_done_something_to_hurt_yourself ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.purposely_done_something_to_hurt_yourself ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("purposely_done_something_to_hurt_yourself") ===
                    "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_done_something_to_hurt_yourself"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_done_something_to_hurt_yourself
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_done_something_to_hurt_yourself
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_done_something_to_hurt_yourself
                                }
                                hasError={
                                    !!errors.describe_done_something_to_hurt_yourself
                                }
                                errorText={
                                    errors
                                        .describe_done_something_to_hurt_yourself
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`7. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.heard_voices_telling_you_to_hurt_yourself
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.heard_voices_telling_you_to_hurt_yourself
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "heard_voices_telling_you_to_hurt_yourself"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.heard_voices_telling_you_to_hurt_yourself ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "heard_voices_telling_you_to_hurt_yourself"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.heard_voices_telling_you_to_hurt_yourself ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.heard_voices_telling_you_to_hurt_yourself ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("heard_voices_telling_you_to_hurt_yourself") ===
                    "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_heard_voices_telling_you_to_hurt_yourself"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_heard_voices_telling_you_to_hurt_yourself
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_heard_voices_telling_you_to_hurt_yourself
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_heard_voices_telling_you_to_hurt_yourself
                                }
                                hasError={
                                    !!errors.describe_heard_voices_telling_you_to_hurt_yourself
                                }
                                errorText={
                                    errors
                                        .describe_heard_voices_telling_you_to_hurt_yourself
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`8. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.had_relatives_who_attempted_or_commited_sucide
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.had_relatives_who_attempted_or_committed_suicide
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "had_relatives_who_attempted_or_committed_suicide"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.had_relatives_who_attempted_or_committed_suicide ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "had_relatives_who_attempted_or_committed_suicide"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.had_relatives_who_attempted_or_committed_suicide ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.had_relatives_who_attempted_or_committed_suicide ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch(
                        "had_relatives_who_attempted_or_committed_suicide"
                    ) === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_had_relatives_who_attempted_or_committed_suicide"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_had_relatives_who_attempted_or_commited_sucide
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_had_relatives_who_attempted_or_committed_suicide
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_had_relatives_who_attempted_or_committed_suicide
                                }
                                hasError={
                                    !!errors.describe_had_relatives_who_attempted_or_committed_suicide
                                }
                                errorText={
                                    errors
                                        .describe_had_relatives_who_attempted_or_committed_suicide
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`9. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.had_thoughts_of_killing_or_seriously_hurting_someone
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.had_thoughts_of_killing_or_seriously_hurting_someone
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "had_thoughts_of_killing_or_seriously_hurting_someone"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.had_thoughts_of_killing_or_seriously_hurting_someone ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "had_thoughts_of_killing_or_seriously_hurting_someone"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.had_thoughts_of_killing_or_seriously_hurting_someone ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.had_thoughts_of_killing_or_seriously_hurting_someone ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch(
                        "had_thoughts_of_killing_or_seriously_hurting_someone"
                    ) === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_had_thoughts_of_killing_or_seriously_hurting_someone"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                }
                                hasError={
                                    !!errors.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                }
                                errorText={
                                    errors
                                        .describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`10. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.heard_voices_telling_you_to_hurt_others
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.heard_voices_telling_you_to_hurt_others
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "heard_voices_telling_you_to_hurt_others"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.heard_voices_telling_you_to_hurt_others ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "heard_voices_telling_you_to_hurt_others"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.heard_voices_telling_you_to_hurt_others ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.heard_voices_telling_you_to_hurt_others ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("heard_voices_telling_you_to_hurt_others") ===
                    "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_heard_voices_telling_you_to_hurt_others"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_heard_voices_telling_you_to_hurt_others
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_heard_voices_telling_you_to_hurt_others
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_heard_voices_telling_you_to_hurt_others
                                }
                                hasError={
                                    !!errors.describe_heard_voices_telling_you_to_hurt_others
                                }
                                errorText={
                                    errors
                                        .describe_heard_voices_telling_you_to_hurt_others
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`11. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.hurt_someone_or_destroyed_property_on_purpose
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.hurt_someone_or_destroyed_property_on_purpose
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "hurt_someone_or_destroyed_property_on_purpose"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.hurt_someone_or_destroyed_property_on_purpose ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "hurt_someone_or_destroyed_property_on_purpose"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.hurt_someone_or_destroyed_property_on_purpose ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.hurt_someone_or_destroyed_property_on_purpose ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("hurt_someone_or_destroyed_property_on_purpose") ===
                    "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_hurt_someone_or_destroyed_property_on_purpose"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_hurt_someone_or_destroyed_property_on_purpose
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_hurt_someone_or_destroyed_property_on_purpose
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_hurt_someone_or_destroyed_property_on_purpose
                                }
                                hasError={
                                    !!errors.describe_hurt_someone_or_destroyed_property_on_purpose
                                }
                                errorText={
                                    errors
                                        .describe_hurt_someone_or_destroyed_property_on_purpose
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`12. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.slapped_kicked_punched_someone_with_intent_to_harm
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.slapped_kicked_punched_someone_with_intent_to_harm
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "slapped_kicked_punched_someone_with_intent_to_harm"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.slapped_kicked_punched_someone_with_intent_to_harm ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "slapped_kicked_punched_someone_with_intent_to_harm"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.slapped_kicked_punched_someone_with_intent_to_harm ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.slapped_kicked_punched_someone_with_intent_to_harm ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch(
                        "slapped_kicked_punched_someone_with_intent_to_harm"
                    ) === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_slapped_kicked_punched_someone_with_intent_to_harm"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                }
                                hasError={
                                    !!errors.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                }
                                errorText={
                                    errors
                                        .describe_slapped_kicked_punched_someone_with_intent_to_harm
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`13. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.been_arrested_or_detained_for_violent_behavior
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.been_arrested_or_detained_for_violent_behavior
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register(
                                "been_arrested_or_detained_for_violent_behavior"
                            )}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_arrested_or_detained_for_violent_behavior ===
                                "No"
                            }
                        />
                        <RadioInput
                            {...register(
                                "been_arrested_or_detained_for_violent_behavior"
                            )}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_arrested_or_detained_for_violent_behavior ===
                                "Yes"
                            }
                        />
                    </div>
                    {errors.been_arrested_or_detained_for_violent_behavior ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("been_arrested_or_detained_for_violent_behavior") ===
                    "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_been_arrested_or_detained_for_violent_behavior"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_been_arrested_or_detained_for_violent_behavior
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_been_arrested_or_detained_for_violent_behavior
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_been_arrested_or_detained_for_violent_behavior
                                }
                                hasError={
                                    !!errors.describe_been_arrested_or_detained_for_violent_behavior
                                }
                                errorText={
                                    errors
                                        .describe_been_arrested_or_detained_for_violent_behavior
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`14. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.been_to_jail_for_any_reason
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.been_to_jail_for_any_reason
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register("been_to_jail_for_any_reason")}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_to_jail_for_any_reason === "No"
                            }
                        />
                        <RadioInput
                            {...register("been_to_jail_for_any_reason")}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_to_jail_for_any_reason === "Yes"
                            }
                        />
                    </div>
                    {errors.been_to_jail_for_any_reason ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("been_to_jail_for_any_reason") === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_been_to_jail_for_any_reason"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_been_to_jail_for_any_reason
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_been_to_jail_for_any_reason
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_been_to_jail_for_any_reason
                                }
                                hasError={
                                    !!errors.describe_been_to_jail_for_any_reason
                                }
                                errorText={
                                    errors.describe_been_to_jail_for_any_reason
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`15. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.been_on_probation_for_any_reason
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.been_on_probation_for_any_reason
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register("been_on_probation_for_any_reason")}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_on_probation_for_any_reason === "No"
                            }
                        />
                        <RadioInput
                            {...register("been_on_probation_for_any_reason")}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.been_on_probation_for_any_reason === "Yes"
                            }
                        />
                    </div>
                    {errors.been_on_probation_for_any_reason ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("been_on_probation_for_any_reason") === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_been_on_probation_for_any_reason"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_been_on_probation_for_any_reason
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_been_on_probation_for_any_reason
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_been_on_probation_for_any_reason
                                }
                                hasError={
                                    !!errors.describe_been_on_probation_for_any_reason
                                }
                                errorText={
                                    errors
                                        .describe_been_on_probation_for_any_reason
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {`16. ${
                            isMinor
                                ? MINOR_RISK_ASSESSMENT_LABELS.do_you_have_access_to_guns
                                : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.do_you_have_access_to_guns
                        }`}
                    </p>
                    <div className="note-radios">
                        <RadioInput
                            {...register("do_you_have_access_to_guns")}
                            label="No"
                            value="No"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.do_you_have_access_to_guns === "No"
                            }
                        />
                        <RadioInput
                            {...register("do_you_have_access_to_guns")}
                            label="Yes"
                            value="Yes"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.do_you_have_access_to_guns === "Yes"
                            }
                        />
                    </div>
                    {errors.do_you_have_access_to_guns ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("do_you_have_access_to_guns") === "Yes" ? (
                        <div className="fg">
                            <TextArea
                                {...register(
                                    "describe_do_you_have_access_to_guns"
                                )}
                                label={
                                    isMinor
                                        ? MINOR_RISK_ASSESSMENT_LABELS.describe_do_you_have_access_to_guns
                                        : INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.describe_do_you_have_access_to_guns
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.describe_do_you_have_access_to_guns
                                }
                                hasError={
                                    !!errors.describe_do_you_have_access_to_guns
                                }
                                errorText={
                                    errors.describe_do_you_have_access_to_guns
                                        ?.message
                                }
                            />
                        </div>
                    ) : null}
                    {isMinor && (
                        <>
                            <p className="note-label-req">
                                {`17. ${MINOR_RISK_ASSESSMENT_LABELS.ever_run_away_from_home}`}
                            </p>
                            <div className="note-radios">
                                <RadioInput
                                    {...register("ever_run_away_from_home")}
                                    label="No"
                                    value="No"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.ever_run_away_from_home === "No"
                                    }
                                />
                                <RadioInput
                                    {...register("ever_run_away_from_home")}
                                    label="Yes"
                                    value="Yes"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.ever_run_away_from_home === "Yes"
                                    }
                                />
                            </div>
                            {errors.ever_run_away_from_home ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch("ever_run_away_from_home") === "Yes" ? (
                                <div className="fg">
                                    <TextArea
                                        {...register(
                                            "describe_ever_run_away_from_home"
                                        )}
                                        label={
                                            MINOR_RISK_ASSESSMENT_LABELS.describe_ever_run_away_from_home
                                        }
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.describe_ever_run_away_from_home
                                        }
                                        hasError={
                                            !!errors.describe_ever_run_away_from_home
                                        }
                                        errorText={
                                            errors
                                                .describe_ever_run_away_from_home
                                                ?.message
                                        }
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                {`18. ${MINOR_RISK_ASSESSMENT_LABELS.ever_been_bullied_or_bullied_someone}`}
                            </p>
                            <div className="note-radios">
                                <RadioInput
                                    {...register(
                                        "ever_been_bullied_or_bullied_someone"
                                    )}
                                    label="No"
                                    value="No"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.ever_been_bullied_or_bullied_someone ===
                                        "No"
                                    }
                                />
                                <RadioInput
                                    {...register(
                                        "ever_been_bullied_or_bullied_someone"
                                    )}
                                    label="Yes"
                                    value="Yes"
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.ever_been_bullied_or_bullied_someone ===
                                        "Yes"
                                    }
                                />
                            </div>
                            {errors.ever_been_bullied_or_bullied_someone ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch("ever_been_bullied_or_bullied_someone") ===
                            "Yes" ? (
                                <div className="fg">
                                    <TextArea
                                        {...register(
                                            "describe_ever_been_bullied_or_bullied_someone"
                                        )}
                                        label={
                                            MINOR_RISK_ASSESSMENT_LABELS.describe_ever_been_bullied_or_bullied_someone
                                        }
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.describe_ever_been_bullied_or_bullied_someone
                                        }
                                        hasError={
                                            !!errors.describe_ever_been_bullied_or_bullied_someone
                                        }
                                        errorText={
                                            errors
                                                .describe_ever_been_bullied_or_bullied_someone
                                                ?.message
                                        }
                                    />
                                </div>
                            ) : null}
                        </>
                    )}
                </section>
                <section id="step-six" className="mb-10">
                    <h3 className="note-label">Mental status exam</h3>
                    <div className="mb-[--spacing-form-items]">
                        <Accordion
                            buttonPosition="left"
                            header={
                                <span className="note-label-req !mb-0">
                                    {
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.mental_health_appearance
                                    }
                                </span>
                            }
                            content={
                                <div className="mt-[--spacing-form-items]">
                                    <div className="note-checkboxes-list !mb-0">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_appearance"
                                            )}
                                            label="Neat"
                                            value="Neat"
                                            defaultChecked={isCheckboxChecked(
                                                "Neat",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_appearance"
                                            )}
                                            label="Unkempt"
                                            value="Unkempt"
                                            defaultChecked={isCheckboxChecked(
                                                "Unkempt",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_appearance"
                                            )}
                                            label="Thin"
                                            value="Thin"
                                            defaultChecked={isCheckboxChecked(
                                                "Thin",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_appearance"
                                            )}
                                            label="Average"
                                            value="Average"
                                            defaultChecked={isCheckboxChecked(
                                                "Average",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_appearance"
                                            )}
                                            label="Overweight"
                                            value="Overweight"
                                            defaultChecked={isCheckboxChecked(
                                                "Overweight",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_appearance"
                                            )}
                                            label="Pale"
                                            value="Pale"
                                            defaultChecked={isCheckboxChecked(
                                                "Pale",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_appearance"
                                            )}
                                            label="Tanned"
                                            value="Tanned"
                                            defaultChecked={isCheckboxChecked(
                                                "Tanned",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    {errors.mental_health_appearance ? (
                        <div className="note-error !mt-0">
                            <p>Please select at least one option</p>
                        </div>
                    ) : null}
                    <div className="mb-[--spacing-form-items]">
                        <Accordion
                            buttonPosition="left"
                            header={
                                <span className="note-label-req !mb-0">
                                    {
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.mental_health_movement
                                    }
                                </span>
                            }
                            content={
                                <div className="mt-[--spacing-form-items]">
                                    <div className="note-checkboxes-list !mb-0">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_movement"
                                            )}
                                            label="Coordinated"
                                            value="Coordinated"
                                            defaultChecked={isCheckboxChecked(
                                                "Coordinated",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_movement"
                                            )}
                                            label="Uncoordinated"
                                            value="Uncoordinated"
                                            defaultChecked={isCheckboxChecked(
                                                "Uncoordinated",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_movement"
                                            )}
                                            label="Mannerisms/oddities"
                                            value="Mannerisms/oddities"
                                            defaultChecked={isCheckboxChecked(
                                                "Mannerisms/oddities",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_movement"
                                            )}
                                            label="Good eye contact"
                                            value="Good eye contact"
                                            defaultChecked={isCheckboxChecked(
                                                "Good eye contact",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_movement"
                                            )}
                                            label="Kept eyes downcast"
                                            value="Kept eyes downcast"
                                            defaultChecked={isCheckboxChecked(
                                                "Kept eyes downcast",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_movement"
                                            )}
                                            label="Stared into space"
                                            value="Stared into space"
                                            defaultChecked={isCheckboxChecked(
                                                "Stared into space",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    {errors.mental_health_movement ? (
                        <div className="note-error !mt-0">
                            <p>Please select at least one option</p>
                        </div>
                    ) : null}
                    <div className="mb-[--spacing-form-items]">
                        <Accordion
                            buttonPosition="left"
                            header={
                                <span className="note-label-req !mb-0">
                                    {
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.mental_health_speech_behaviour
                                    }
                                </span>
                            }
                            content={
                                <div className="mt-[--spacing-form-items]">
                                    <div className="note-checkboxes-list !mb-0">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Unintelligible"
                                            value="Unintelligible"
                                            defaultChecked={isCheckboxChecked(
                                                "Unintelligible",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Stammer/stutter"
                                            value="Stammer/stutter"
                                            defaultChecked={isCheckboxChecked(
                                                "Stammer/stutter",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Mumbles"
                                            value="Mumbles"
                                            defaultChecked={isCheckboxChecked(
                                                "Mumbles",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Clear"
                                            value="Clear"
                                            defaultChecked={isCheckboxChecked(
                                                "Clear",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Precise"
                                            value="Precise"
                                            defaultChecked={isCheckboxChecked(
                                                "Precise",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Strident"
                                            value="Strident"
                                            defaultChecked={isCheckboxChecked(
                                                "Strident",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Distant"
                                            value="Distant"
                                            defaultChecked={isCheckboxChecked(
                                                "Distant",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Squeaky"
                                            value="Squeaky"
                                            defaultChecked={isCheckboxChecked(
                                                "Squeaky",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Squeaky"
                                            value="Squeaky"
                                            defaultChecked={isCheckboxChecked(
                                                "Squeaky",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Soft"
                                            value="Soft"
                                            defaultChecked={isCheckboxChecked(
                                                "Soft",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Weak"
                                            value="Weak"
                                            defaultChecked={isCheckboxChecked(
                                                "Weak",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Normal"
                                            value="Normal"
                                            defaultChecked={isCheckboxChecked(
                                                "Normal",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Strident"
                                            value="Strident"
                                            defaultChecked={isCheckboxChecked(
                                                "Strident",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Hurried"
                                            value="Hurried"
                                            defaultChecked={isCheckboxChecked(
                                                "Hurried",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_speech_behaviour"
                                            )}
                                            label="Loud"
                                            value="Loud"
                                            defaultChecked={isCheckboxChecked(
                                                "Loud",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    {errors.mental_health_speech_behaviour ? (
                        <div className="note-error !mt-0">
                            <p>Please select at least one option</p>
                        </div>
                    ) : null}
                    <div className="mb-[--spacing-form-items]">
                        <Accordion
                            buttonPosition="left"
                            header={
                                <span className="note-label-req !mb-0">
                                    {
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.mental_health_affect_mood
                                    }
                                </span>
                            }
                            content={
                                <div className="mt-[--spacing-form-items]">
                                    <div className="note-checkboxes-list !mb-0">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Blunted"
                                            value="Blunted"
                                            defaultChecked={isCheckboxChecked(
                                                "Blunted",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Constricted"
                                            value="Constricted"
                                            defaultChecked={isCheckboxChecked(
                                                "Constricted",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Normal"
                                            value="Normal"
                                            defaultChecked={isCheckboxChecked(
                                                "Normal",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Broad"
                                            value="Broad"
                                            defaultChecked={isCheckboxChecked(
                                                "Broad",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Inappropriate"
                                            value="Inappropriate"
                                            defaultChecked={isCheckboxChecked(
                                                "Inappropriate",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Indifferent to problems"
                                            value="Indifferent to problems"
                                            defaultChecked={isCheckboxChecked(
                                                "Indifferent to problems",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Congruent"
                                            value="Congruent"
                                            defaultChecked={isCheckboxChecked(
                                                "Congruent",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Irritated"
                                            value="Irritated"
                                            defaultChecked={isCheckboxChecked(
                                                "Irritated",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Hostile"
                                            value="Hostile"
                                            defaultChecked={isCheckboxChecked(
                                                "Hostile",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Flushing"
                                            value="flushing"
                                            defaultChecked={isCheckboxChecked(
                                                "flushing",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Dizziness"
                                            value="Dizziness"
                                            defaultChecked={isCheckboxChecked(
                                                "Dizziness",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Can't sit still"
                                            value="Can't sit still"
                                            defaultChecked={isCheckboxChecked(
                                                "Can't sit still",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Panicked"
                                            value="Panicked"
                                            defaultChecked={isCheckboxChecked(
                                                "Panicked",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Fearful"
                                            value="Fearful"
                                            defaultChecked={isCheckboxChecked(
                                                "Fearful",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Wretched"
                                            value="Wretched"
                                            defaultChecked={isCheckboxChecked(
                                                "Wretched",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Melancholy"
                                            value="Melancholy"
                                            defaultChecked={isCheckboxChecked(
                                                "Melancholy",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Sad"
                                            value="Sad"
                                            defaultChecked={isCheckboxChecked(
                                                "Sad",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Hopeless"
                                            value="Hopeless"
                                            defaultChecked={isCheckboxChecked(
                                                "Hopeless",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Bored"
                                            value="Bored"
                                            defaultChecked={isCheckboxChecked(
                                                "Bored",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Sorrow"
                                            value="Sorrow"
                                            defaultChecked={isCheckboxChecked(
                                                "Sorrow",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Grief"
                                            value="Grief"
                                            defaultChecked={isCheckboxChecked(
                                                "Grief",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Flight of ideas"
                                            value="Flight of ideas"
                                            defaultChecked={isCheckboxChecked(
                                                "Flight of ideas",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Manic"
                                            value="Manic"
                                            defaultChecked={isCheckboxChecked(
                                                "Manic",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Hypomanic"
                                            value="Hypomanic"
                                            defaultChecked={isCheckboxChecked(
                                                "Hypomanic",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Ashamed"
                                            value="Ashamed"
                                            defaultChecked={isCheckboxChecked(
                                                "Ashamed",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Embarrassed"
                                            value="Embarrassed"
                                            defaultChecked={isCheckboxChecked(
                                                "Embarrassed",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Apologetic"
                                            value="Apologetic"
                                            defaultChecked={isCheckboxChecked(
                                                "Apologetic",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Calm"
                                            value="Calm"
                                            defaultChecked={isCheckboxChecked(
                                                "Calm",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Guilty"
                                            value="Guilty"
                                            defaultChecked={isCheckboxChecked(
                                                "Guilty",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_affect_mood"
                                            )}
                                            label="Flat"
                                            value="Flat"
                                            defaultChecked={isCheckboxChecked(
                                                "Flat",
                                                noteDetails.note_content
                                                    ?.mental_health_affect_mood
                                            )}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    {errors.mental_health_affect_mood ? (
                        <div className="note-error !mt-0">
                            <p>Please select at least one option</p>
                        </div>
                    ) : null}
                    <div className="mb-[--spacing-form-items]">
                        <Accordion
                            buttonPosition="left"
                            header={
                                <span className="note-label-req !mb-0">
                                    {
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.mental_health_attention_concentration_effort
                                    }
                                </span>
                            }
                            content={
                                <div className="mt-[--spacing-form-items]">
                                    <div className="note-checkboxes-list !mb-0">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Sluggish"
                                            value="Sluggish"
                                            defaultChecked={isCheckboxChecked(
                                                "Sluggish",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Flat"
                                            value="Flat"
                                            defaultChecked={isCheckboxChecked(
                                                "Flat",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Distractible"
                                            value="Distractible"
                                            defaultChecked={isCheckboxChecked(
                                                "Distractible",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Normal energy"
                                            value="Normal energy"
                                            defaultChecked={isCheckboxChecked(
                                                "Normal energy",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Eager"
                                            value="Eager"
                                            defaultChecked={isCheckboxChecked(
                                                "Eager",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Indifferent"
                                            value="Indifferent"
                                            defaultChecked={isCheckboxChecked(
                                                "Indifferent",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Scattered"
                                            value="Scattered"
                                            defaultChecked={isCheckboxChecked(
                                                "Scattered",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Baffled"
                                            value="Baffled"
                                            defaultChecked={isCheckboxChecked(
                                                "Baffled",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Perplexed"
                                            value="Perplexed"
                                            defaultChecked={isCheckboxChecked(
                                                "Perplexed",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Hurried"
                                            value="Hurried"
                                            defaultChecked={isCheckboxChecked(
                                                "Hurried",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Organized"
                                            value="Organized"
                                            defaultChecked={isCheckboxChecked(
                                                "Organized",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Rigid"
                                            value="Rigid"
                                            defaultChecked={isCheckboxChecked(
                                                "Rigid",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Pleasant"
                                            value="Pleasant"
                                            defaultChecked={isCheckboxChecked(
                                                "Pleasant",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Cooperative"
                                            value="Cooperative"
                                            defaultChecked={isCheckboxChecked(
                                                "Cooperative",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Dependent"
                                            value="Dependent"
                                            defaultChecked={isCheckboxChecked(
                                                "Dependent",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Abusive"
                                            value="Abusive"
                                            defaultChecked={isCheckboxChecked(
                                                "Abusive",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Superior"
                                            value="Superior"
                                            defaultChecked={isCheckboxChecked(
                                                "Superior",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Stubborn"
                                            value="Stubborn"
                                            defaultChecked={isCheckboxChecked(
                                                "Stubborn",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Belligerent"
                                            value="Belligerent"
                                            defaultChecked={isCheckboxChecked(
                                                "Belligerent",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Argumentative"
                                            value="Argumentative"
                                            defaultChecked={isCheckboxChecked(
                                                "Argumentative",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Hostile"
                                            value="Hostile"
                                            defaultChecked={isCheckboxChecked(
                                                "Hostile",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Demanding"
                                            value="Demanding"
                                            defaultChecked={isCheckboxChecked(
                                                "Demanding",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Resentful"
                                            value="Resentful"
                                            defaultChecked={isCheckboxChecked(
                                                "Resentful",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Surly"
                                            value="Surly"
                                            defaultChecked={isCheckboxChecked(
                                                "Surly",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Guarded"
                                            value="Guarded"
                                            defaultChecked={isCheckboxChecked(
                                                "Guarded",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_attention_concentration_effort"
                                            )}
                                            label="Indifferent"
                                            value="Indifferent"
                                            defaultChecked={isCheckboxChecked(
                                                "Indifferent",
                                                noteDetails.note_content
                                                    ?.mental_health_appearance
                                            )}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    {errors.mental_health_attention_concentration_effort ? (
                        <div className="note-error !mt-0">
                            <p>Please select at least one option</p>
                        </div>
                    ) : null}
                    <div className="mb-[--spacing-form-items]">
                        <Accordion
                            buttonPosition="left"
                            header={
                                <span className="note-label-req !mb-0">
                                    {
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.mental_health_presentation_of_self
                                    }
                                </span>
                            }
                            content={
                                <div className="mt-[--spacing-form-items]">
                                    <div className="note-checkboxes-list !mb-0">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Withdrawn"
                                            value="Withdrawn"
                                            defaultChecked={isCheckboxChecked(
                                                "Withdrawn",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Threatened"
                                            value="Threatened"
                                            defaultChecked={isCheckboxChecked(
                                                "Threatened",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Vulnerable"
                                            value="Vulnerable"
                                            defaultChecked={isCheckboxChecked(
                                                "Vulnerable",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Shy"
                                            value="Shy"
                                            defaultChecked={isCheckboxChecked(
                                                "Shy",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Friendly"
                                            value="Friendly"
                                            defaultChecked={isCheckboxChecked(
                                                "Friendly",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Confident"
                                            value="Confident"
                                            defaultChecked={isCheckboxChecked(
                                                "Confident",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Grandiose"
                                            value="Grandiose"
                                            defaultChecked={isCheckboxChecked(
                                                "Grandiose",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Humble"
                                            value="Humble"
                                            defaultChecked={isCheckboxChecked(
                                                "Humble",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Self-doubting"
                                            value="Self-doubting"
                                            defaultChecked={isCheckboxChecked(
                                                "Self-doubting",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Hopeful"
                                            value="Hopeful"
                                            defaultChecked={isCheckboxChecked(
                                                "Hopeful",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Pessimistic"
                                            value="Pessimistic"
                                            defaultChecked={isCheckboxChecked(
                                                "Pessimistic",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Overindulgent"
                                            value="Overindulgent"
                                            defaultChecked={isCheckboxChecked(
                                                "Overindulgent",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Warm hearted"
                                            value="Warm hearted"
                                            defaultChecked={isCheckboxChecked(
                                                "Warm hearted",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Distant"
                                            value="Distant"
                                            defaultChecked={isCheckboxChecked(
                                                "Distant",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_presentation_of_self"
                                            )}
                                            label="Harsh"
                                            value="Harsh"
                                            defaultChecked={isCheckboxChecked(
                                                "Harsh",
                                                noteDetails.note_content
                                                    ?.mental_health_presentation_of_self
                                            )}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    {errors.mental_health_presentation_of_self ? (
                        <div className="note-error !mt-0">
                            <p>Please select at least one option</p>
                        </div>
                    ) : null}
                    <div className="mb-[--spacing-form-items]">
                        <Accordion
                            buttonPosition="left"
                            header={
                                <span className="note-label-req !mb-0">
                                    {
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.mental_health_cognition
                                    }
                                </span>
                            }
                            content={
                                <div className="mt-[--spacing-form-items]">
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="No insight"
                                            value="No insight"
                                            defaultChecked={isCheckboxChecked(
                                                "No insight",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Some insight"
                                            value="Some insight"
                                            defaultChecked={isCheckboxChecked(
                                                "Some insight",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Full insight"
                                            value="Full insight"
                                            defaultChecked={isCheckboxChecked(
                                                "Full insight",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Sluggish recall"
                                            value="Sluggish recall"
                                            defaultChecked={isCheckboxChecked(
                                                "Sluggish recall",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Low intelligent"
                                            value="Low intelligent"
                                            defaultChecked={isCheckboxChecked(
                                                "Low intelligent",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Intelligent"
                                            value="Intelligent"
                                            defaultChecked={isCheckboxChecked(
                                                "Intelligent",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Defective reality"
                                            value="Defective reality"
                                            defaultChecked={isCheckboxChecked(
                                                "Defective reality",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Impoverished stream of thoughts"
                                            value="Impoverished stream of thoughts"
                                            defaultChecked={isCheckboxChecked(
                                                "Impoverished stream of thoughts",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Normal stream of thoughts"
                                            value="Normal stream of thoughts"
                                            defaultChecked={isCheckboxChecked(
                                                "Normal stream of thoughts",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Incoherent thought process"
                                            value="Incoherent thought process"
                                            defaultChecked={isCheckboxChecked(
                                                "Incoherent thought process",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Loose thought process"
                                            value="Loose thought process"
                                            defaultChecked={isCheckboxChecked(
                                                "Loose thought process",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Clear thought process"
                                            value="Clear thought process"
                                            defaultChecked={isCheckboxChecked(
                                                "Clear thought process",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Flight of ideas"
                                            value="Flight of ideas"
                                            defaultChecked={isCheckboxChecked(
                                                "Flight of ideas",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Indecisive"
                                            value="Indecisive"
                                            defaultChecked={isCheckboxChecked(
                                                "Indecisive",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Decisive"
                                            value="Decisive"
                                            defaultChecked={isCheckboxChecked(
                                                "Decisive",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Lack of common sense"
                                            value="Lack of common sense"
                                            defaultChecked={isCheckboxChecked(
                                                "Lack of common sense",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Forgetful"
                                            value="Forgetful"
                                            defaultChecked={isCheckboxChecked(
                                                "Forgetful",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Follows through"
                                            value="Follows through"
                                            defaultChecked={isCheckboxChecked(
                                                "Follows through",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Faulty reasoning"
                                            value="Faulty reasoning"
                                            defaultChecked={isCheckboxChecked(
                                                "Faulty reasoning",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Easily confused"
                                            value="Easily confused"
                                            defaultChecked={isCheckboxChecked(
                                                "Easily confused",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Normal abstraction"
                                            value="Normal abstraction"
                                            defaultChecked={isCheckboxChecked(
                                                "Normal abstraction",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Procrastinates"
                                            value="Procrastinates"
                                            defaultChecked={isCheckboxChecked(
                                                "Procrastinates",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Confuses time frames/sequence"
                                            value="Confuses time frames/sequence"
                                            defaultChecked={isCheckboxChecked(
                                                "Confuses time frames/sequence",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Normal memory"
                                            value="Normal memory"
                                            defaultChecked={isCheckboxChecked(
                                                "Normal memory",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Amnesia"
                                            value="Amnesia"
                                            defaultChecked={isCheckboxChecked(
                                                "Amnesia",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Confused present location/date/persons, places"
                                            value="Confused present location/date/persons, places"
                                            defaultChecked={isCheckboxChecked(
                                                "Confused present location/date/persons, places",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Good orientation"
                                            value="Good orientation"
                                            defaultChecked={isCheckboxChecked(
                                                "Good orientation",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group">
                                        <div />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Victimization"
                                            value="Victimization"
                                            defaultChecked={isCheckboxChecked(
                                                "Victimization",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Normal judgement"
                                            value="Normal judgment"
                                            defaultChecked={isCheckboxChecked(
                                                "Normal judgment",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                    <div className="note-checkboxes-list-group justify-content-end">
                                        <div />
                                        <div />
                                        <FormCheckbox
                                            {...register(
                                                "mental_health_cognition"
                                            )}
                                            label="Intact reality"
                                            value="Intact reality"
                                            defaultChecked={isCheckboxChecked(
                                                "Intact reality",
                                                noteDetails.note_content
                                                    ?.mental_health_cognition
                                            )}
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    {errors.mental_health_cognition ? (
                        <div className="note-error">
                            <p>Please select at least one option</p>
                        </div>
                    ) : null}

                    <div>
                        <h3 className="note-label-req">
                            {
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.patients_ability_and_capability_to_respond_to_treatment
                            }
                        </h3>
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    `patients_ability_and_capability_to_respond_to_treatment`
                                )}
                                label="Consistent, good-quality patient-oriented evidence"
                                value="Consistent, good-quality patient-oriented evidence"
                                defaultChecked={isCheckboxChecked(
                                    "Consistent, good-quality patient-oriented evidence",
                                    noteDetails.note_content
                                        ?.patients_ability_and_capability_to_respond_to_treatment
                                )}
                            />
                            <FormCheckbox
                                {...register(
                                    `patients_ability_and_capability_to_respond_to_treatment`
                                )}
                                label="Diminished/poor or limited-quality patient-oriented evidence"
                                value="Diminished/poor or limited-quality patient-oriented evidence"
                                defaultChecked={isCheckboxChecked(
                                    "Diminished/poor or limited-quality patient-oriented evidence",
                                    noteDetails.note_content
                                        ?.patients_ability_and_capability_to_respond_to_treatment
                                )}
                            />
                            {(
                                watch(
                                    `patients_ability_and_capability_to_respond_to_treatment`
                                ) || []
                            )?.includes(
                                "Diminished/poor or limited-quality patient-oriented evidence"
                            ) && (
                                <>
                                    <span className="note-label !mb-0">
                                        {
                                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                        }
                                    </span>
                                    <TextArea
                                        {...register(
                                            `details_for_diminished_poor_limited_quality_patient_oriented_evidence`
                                        )}
                                        defaultValue={
                                            noteDetails.note_content
                                                ?.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                        }
                                        hasError={
                                            !!errors.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                        }
                                        errorText={
                                            errors
                                                .details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                                ?.message
                                        }
                                    />
                                </>
                            )}
                        </div>
                        {errors.patients_ability_and_capability_to_respond_to_treatment ? (
                            <div className="note-error">
                                <p>This field is required</p>
                            </div>
                        ) : null}
                    </div>
                </section>
                <section id="step-seven" className="mb-10">
                    <h3 className="note-header">Summary</h3>
                    <p className="note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.people_present_at_session
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Patient"
                            value="Patient"
                            defaultChecked={isCheckboxChecked(
                                "Patient",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isCheckboxChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Stepfather"
                            value="Stepfather"
                            defaultChecked={isCheckboxChecked(
                                "Stepfather",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Spouse"
                            value="Spouse"
                            defaultChecked={isCheckboxChecked(
                                "Spouse",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Father"
                            value="Father"
                            defaultChecked={isCheckboxChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Stepmother"
                            value="Stepmother"
                            defaultChecked={isCheckboxChecked(
                                "Stepmother",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Partner"
                            value="Partner"
                            defaultChecked={isCheckboxChecked(
                                "Partner",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Guardian"
                            value="Guardian"
                            defaultChecked={isCheckboxChecked(
                                "Guardian",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register("people_present_at_session")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                    </div>
                    {errors.people_present_at_session ? (
                        <div className="note-error">
                            <p>People present at session is required</p>
                        </div>
                    ) : null}
                    <div className="fg">
                        <Input
                            {...register("other_person_present_at_session")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_person_present_at_session
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_person_present_at_session
                            }
                            isOptional
                        />
                    </div>
                    <p className="note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.type_of_treatment_recommended
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Individual"
                            value="Individual"
                            defaultChecked={isCheckboxChecked(
                                "Individual",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Family"
                            value="Family"
                            defaultChecked={isCheckboxChecked(
                                "Family",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Group"
                            value="Group"
                            defaultChecked={isCheckboxChecked(
                                "Group",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register("type_of_treatment_recommended")}
                            label="Collateral/Parenting"
                            value="Collateral/Parenting"
                            defaultChecked={isCheckboxChecked(
                                "Collateral/Parenting",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                    </div>
                    {errors.type_of_treatment_recommended ? (
                        <div className="note-error">
                            <p>Type of treatment recommended is required</p>
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_modality_intervention
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Cognitive/Behavioral"
                            value="Cognitive/Behavioral"
                            defaultChecked={isCheckboxChecked(
                                "Cognitive/Behavioral",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Behavioral Modification"
                            value="Behavioral Modification"
                            defaultChecked={isCheckboxChecked(
                                "Behavioral Modification",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Supportive"
                            value="Supportive"
                            defaultChecked={isCheckboxChecked(
                                "Supportive",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Marital/Couples Therapy "
                            value="Marital/Couples Therapy "
                            defaultChecked={isCheckboxChecked(
                                "Marital/Couples Therapy ",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Family Therapy"
                            value="Family Therapy"
                            defaultChecked={isCheckboxChecked(
                                "Family Therapy",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Stress Management"
                            value="Stress Management"
                            defaultChecked={isCheckboxChecked(
                                "Stress Management",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Psychodynamic"
                            value="Psychodynamic"
                            defaultChecked={isCheckboxChecked(
                                "Psychodynamic",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Parent Training"
                            value="Parent Training"
                            defaultChecked={isCheckboxChecked(
                                "Parent Training",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Crisis Intervention"
                            value="Crisis Intervention"
                            defaultChecked={isCheckboxChecked(
                                "Crisis Intervention",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register("treatment_modality_intervention")}
                            label="Other"
                            value="Other"
                            defaultChecked={isCheckboxChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                    </div>
                    {errors.treatment_modality_intervention ? (
                        <div className="note-error">
                            <p>Treatment modality/intervention is required</p>
                        </div>
                    ) : null}
                    <div className="fg">
                        <Input
                            {...register(
                                "other_treatment_modality_intervention"
                            )}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_treatment_modality_intervention
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_treatment_modality_intervention
                            }
                            isOptional
                        />
                    </div>
                    <p className="note-label">Treatment goals</p>
                    <div className="flex flex-col gap-y-[--spacing-form-items] mb-[--spacing-form-items]">
                        <Input
                            {...register("treatment_goal_one")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_goal_one
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_one
                            }
                            isRequired
                            hasError={!!errors.treatment_goal_one}
                            errorText={errors.treatment_goal_one?.message}
                        />
                        <Input
                            {...register("treatment_goal_two")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_goal_two
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_two
                            }
                            isRequired
                            hasError={!!errors.treatment_goal_two}
                            errorText={errors.treatment_goal_two?.message}
                        />
                        <Input
                            {...register("treatment_goal_three")}
                            label={
                                INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_goal_three
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_three
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.frequency_of_sessions
                        }
                    </h3>
                    <div className="note-radios">
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Weekly"
                            value="Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Bi-Weekly"
                            value="Bi-Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Bi-Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Monthly"
                            value="Monthly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Monthly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Other"
                            value="Other"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Other"
                            }
                        />
                    </div>
                    {errors.frequency_of_sessions ? (
                        <div className="note-error">
                            <p>Frequency of sessions is required</p>
                        </div>
                    ) : null}
                    {watch("frequency_of_sessions") === "Other" && (
                        <div className="fg">
                            <Input
                                {...register("other_frequency_of_sessions")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_frequency_of_sessions
                                }
                                hasError={!!errors.other_frequency_of_sessions}
                                errorText={
                                    errors.other_frequency_of_sessions?.message
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.other_frequency_of_sessions
                                }
                            />
                        </div>
                    )}
                    <div className="fg">
                        <Controller
                            name="date_of_next_session"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={
                                        INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.date_of_next_session
                                    }
                                    onChange={(date) => {
                                        field.onChange(date);
                                        setLocalDateOfNextSession(date);
                                    }}
                                    selected={localDateOfNextSession}
                                    value={
                                        field.value
                                            ? formatDate(field.value, true)
                                            : undefined
                                    }
                                    hasError={!!errors.date_of_next_session}
                                    errorText={
                                        errors.date_of_next_session?.type ===
                                        "typeError"
                                            ? "Invalid date value"
                                            : errors.date_of_next_session
                                                  ?.message
                                    }
                                    shouldCloseOnSelect
                                    isRequired
                                    minDate={
                                        noteDetails.date_of_service
                                            ? addDays(
                                                  parseISO(
                                                      noteDetails.date_of_service
                                                  ),
                                                  1
                                              )
                                            : undefined
                                    }
                                />
                            )}
                        />
                    </div>
                    <h3 className="note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.subjective_report
                        }
                    </h3>
                    <p className="note-label">
                        Please make sure to only include what the client has
                        reported. This should only tie back to what the client
                        reported in the session, re; symptoms, mood/affect,
                        ideations, interpersonal conflicts, etc. Do not include
                        your assessment or interpretation of the clients
                        symptoms here.
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("subjective_report")}
                            defaultValue={
                                noteDetails.note_content?.subjective_report
                            }
                            hasError={!!errors.subjective_report}
                            errorText={errors.subjective_report?.message}
                        />
                    </div>
                    <h3 className="note-label-req">
                        {INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.summary}
                    </h3>
                    <div className="fg">
                        <TextArea
                            {...register("summary")}
                            defaultValue={noteDetails.note_content?.summary}
                            hasError={!!errors.summary}
                            errorText={errors.summary?.message}
                        />
                    </div>
                    <h3 className="note-label-req">
                        {INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.prognosis}
                    </h3>
                    <p className="note-label">
                        Please write at least 1-2 sentences about the likely
                        outcome/development of the symptoms/challenges
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("prognosis")}
                            defaultValue={noteDetails.note_content?.prognosis}
                        />
                    </div>
                    <h3 className="note-label note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.medical_necessity_rationale
                        }
                    </h3>
                    <div className="note-checkboxes">
                        <Controller
                            name="medical_necessity_rationale"
                            control={control}
                            render={({ field }) => {
                                const checkboxValue =
                                    "Symptoms result in clinically significant distress and/or functional impairment warranting outpatient psychotherapy at the recommended frequency";
                                return (
                                    <FormCheckbox
                                        label={checkboxValue}
                                        value={checkboxValue}
                                        checked={
                                            field.value?.includes(
                                                checkboxValue
                                            ) ?? false
                                        }
                                        onChange={(e) =>
                                            field.onChange(
                                                e.target.checked
                                                    ? [checkboxValue]
                                                    : null
                                            )
                                        }
                                    />
                                );
                            }}
                        />
                    </div>
                    {errors.medical_necessity_rationale ? (
                        <div className="note-error">
                            <p>Medical necessity rationale is required</p>
                        </div>
                    ) : null}

                    <h3 className="note-label note-label-req">
                        {
                            INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.risk_review_supervision_plan
                        }
                    </h3>
                    <div className="note-checkboxes">
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Risk reviewed by supervising clinician"
                            value="Risk reviewed by supervising clinician"
                            defaultChecked={isCheckboxChecked(
                                "Risk reviewed by supervising clinician",
                                noteDetails.note_content
                                    ?.risk_review_supervision_plan
                            )}
                        />
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Appropriate level of care determined"
                            value="Appropriate level of care determined"
                            defaultChecked={isCheckboxChecked(
                                "Appropriate level of care determined",
                                noteDetails.note_content
                                    ?.risk_review_supervision_plan
                            )}
                        />
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Risk/safety concerns addressed as clinically indicated"
                            value="Risk/safety concerns addressed as clinically indicated"
                            defaultChecked={
                                isCheckboxChecked(
                                    "Risk/safety concerns addressed as clinically indicated",
                                    noteDetails.note_content
                                        ?.risk_review_supervision_plan
                                ) ||
                                isCheckboxChecked(
                                    "Safety planning addressed as clinically indicated",
                                    noteDetails.note_content
                                        ?.risk_review_supervision_plan
                                )
                            }
                        />
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Formal safety plan completed/updated"
                            value="Formal safety plan completed/updated"
                            defaultChecked={isCheckboxChecked(
                                "Formal safety plan completed/updated",
                                noteDetails.note_content
                                    ?.risk_review_supervision_plan
                            )}
                        />
                    </div>
                    {shouldShowFormalSafetyPlan ? (
                        <div className="fg">
                            <TextArea
                                {...register("formal_safety_plan_details")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.formal_safety_plan_details
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.formal_safety_plan_details
                                }
                            />
                        </div>
                    ) : null}
                    <div className="note-checkboxes">
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Higher level of care considered/recommended"
                            value="Higher level of care considered/recommended"
                            defaultChecked={isCheckboxChecked(
                                "Higher level of care considered/recommended",
                                noteDetails.note_content
                                    ?.risk_review_supervision_plan
                            )}
                        />
                    </div>
                    {shouldShowHigherLevelOfCare ? (
                        <div className="fg">
                            <TextArea
                                {...register("higher_level_of_care_details")}
                                label={
                                    INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.higher_level_of_care_details
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.higher_level_of_care_details
                                }
                            />
                        </div>
                    ) : null}
                    {errors.risk_review_supervision_plan ? (
                        <div className="note-error">
                            <p>Risk review supervision plan is required</p>
                        </div>
                    ) : null}
                </section>

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
